// AcademicAssistant.js
import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import "./AcademicAssistant.css";
import AgniTutorMathRenderer from './AgniTutorMathRenderer';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import 'katex/dist/katex.min.css'; 
import WelcomePanel from "./WelcomePanel";
import FeedbackModal from "./FeedbackModal";

import "@fortawesome/fontawesome-free/css/all.min.css";

/* ---------------- Configuration ---------------- */
// NOTE: API key removed from frontend for security/cost reasons
const apiKey = null;
const API_BASE =
  process.env.REACT_APP_API_BASE || "http://localhost:5001";

/* ---------------- System instruction ---------------- */
const SYSTEM_INSTRUCTION = `You are "IndLawAI" — an AI-powered Indian legal assistant. Help users understand laws, legal rights, legal procedures, government regulations, and legal documents in simple language. Follow these rules strictly.`;

/* ---------------- Storage keys ---------------- */
const SESSIONS_KEY = "aa_sessions_v1";
const COUNTS_KEY = "aa_like_counts_v1";
const HISTORY_RETENTION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/* ---------------- Helpers ---------------- */
function removeAutoSymbols(str = "", forSolver = false) {
  if (!str) return str;

  try {
    let out = String(str);

    /* ==================================================
       🔥 IMPORTANT RULE
       - Display mode (forSolver = false):
         → DO NOT TOUCH LaTeX at all
       - Solver mode (forSolver = true):
         → Normalize symbols for calculation only
    ================================================== */

    if (!forSolver) {
      // 🔒 DO NOT touch LaTeX blocks
      if (out.includes("\\frac") || out.includes("$")) {
        return out;
      }

      return out
        .replace(/[\uFE00-\uFEFF\u200B-\u200F\u202A-\u202E]/g, "")
        .replace(/\r\n/g, "\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
    }

    /* ==================================================
       ✅ SOLVER-ONLY NORMALIZATION
       (SAFE to convert math symbols here)
    ================================================== */

    out = out
      // powers
      .replace(/²/g, "^2")
      .replace(/³/g, "^3")
      .replace(/⁴/g, "^4")

      // operators
      .replace(/−/g, "-")
      .replace(/×/g, "*")
      .replace(/÷/g, "/")

      // arrows
      .replace(/\\rightarrow/g, "->")
      .replace(/→/g, "->");

    // --- Subscripts → normal digits (solver only) ---
    const subMap = {
      "₀": "0", "₁": "1", "₂": "2", "₃": "3", "₄": "4",
      "₅": "5", "₆": "6", "₇": "7", "₈": "8", "₉": "9"
    };
    out = out.replace(/[\u2080-\u2089]/g, m => subMap[m] ?? m);

    // --- Cleanup invisible chars ---
    out = out.replace(/[\uFE00-\uFEFF\u200B-\u200F\u202A-\u202E]/g, "");

    // --- Normalize spacing ---
    out = out
      .replace(/\r\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .replace(/[ \t]{2,}/g, " ")
      .trim();

    return out;

  } catch (e) {
    console.warn("removeAutoSymbols failed", e);
    return String(str);
  }
}




function sanitizeMessageInSession(msg) {
  try {
    if (!msg || typeof msg !== "object") return msg;
    const cloned = { ...msg };
    if (typeof cloned.text === "string") cloned.text = removeAutoSymbols(cloned.text);
    return cloned;
  } catch (e) {
    return msg;
  }
}

function loadJSON(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}
function saveJSON(key, val) {
  try {
    localStorage.setItem(key, JSON.stringify(val));
  } catch {}
}
function loadSessions() {
  try {
    const all = loadJSON(SESSIONS_KEY, []);
    const now = Date.now();
    const fresh = all
      .filter((s) => now - s.createdAt <= HISTORY_RETENTION_MS)
      .map((s) => {
        try {
          const cloned = { ...s };
          if (Array.isArray(cloned.messages)) cloned.messages = cloned.messages.map((m) => sanitizeMessageInSession(m));
          return cloned;
        } catch {
          return s;
        }
      });
    if (fresh.length !== all.length) saveJSON(SESSIONS_KEY, fresh);
    return fresh;
  } catch {
    return [];
  }
}
function saveSessions(sessions) {
  try {
    const sanitized = sessions.map((s) => {
      try {
        const c = { ...s };
        if (Array.isArray(c.messages)) c.messages = c.messages.map((m) => sanitizeMessageInSession(m));
        return c;
      } catch {
        return s;
      }
    });
    saveJSON(SESSIONS_KEY, sanitized);
  } catch {}
}
function loadCounts() {
  return loadJSON(COUNTS_KEY, {});
}
function saveCounts(obj) {
  saveJSON(COUNTS_KEY, obj);
}
function makeSession() {
  return {
    id: `${Date.now()}-${Math.floor(Math.random() * 10000)}`,
    createdAt: Date.now(),
    messages: [],
  };
}

/**
 * Converts a File object to a Base64 data URI string.
 * KEEP FOR LOCAL PREVIEW ONLY - DO NOT SEND THIS DATA URI TO THE MODEL.
 * @param {File} file 
 * @returns {Promise<string>} Base64 data URI (e.g., "data:image/jpeg;base64,...")
 */
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * fileToGenerativePart (SAFE) - Returns only a small placeholder for the image.
 * This prevents huge payloads and costs. If you need the model to see the image,
 * upload to the server and use a signed URL (server then forwards to Google).
 */
function fileToGenerativePart(file) {
  try {
    // Accept either a File-like object from input or an uploaded file entry { name, url, type, size }
    if (file && (file.url || file.image_url)) {
      const filename = file.name || file.filename || "image";
      const url = file.url || file.image_url;
      return Promise.resolve({ inlineData: undefined, parts: [{ text: `[ImageURL:${url}]` }] });
    }

    const filename = file && (file.name || file.fileName || file.filename) ? (file.name || file.fileName || file.filename) : "image";
    return Promise.resolve({ inlineData: undefined, parts: [{ text: `[Image: ${filename}]` }] });
  } catch (e) {
    return Promise.resolve({ inlineData: undefined, parts: [{ text: `[Image]` }] });
  }
}

function isCode(text) {
  if (!text) return false;

  const t = text.trim();
  const lines = t.split("\n").filter(Boolean);

  if (lines.length < 2) return false;

  return (
    /#include|System\.out|console\.log|def\s+|public\s+class|<html|SELECT\s+|function\s+/i.test(t) ||
    (t.includes("{") && t.includes("}"))
  );
}



function extractCode(text) {
  return text.replace(/```/g, "").trim();
}

function detectLanguageFromText(text) {
  if (!text) return null;
  const t = text.trim();
  if (t.match(/^\s*</)) return "html";
  if (t.includes("def ") || t.includes("import ")) return "python";
  if (t.includes("function ") || t.includes("console.log")) return "javascript";
  return null;
}

function detectLang(text) {
  if (!text) return "text";

  if (/#include/.test(text)) return "c";
  if (/System\.out/.test(text)) return "java";
  if (/def\s+/.test(text)) return "python";
  if (/console\.log|function\s+/.test(text)) return "javascript";
  if (/<html|<div|<span/.test(text)) return "markup";
  if (/SELECT\s+.*FROM/i.test(text)) return "sql";
  if (/using\s+System|namespace\s+/i.test(text)) return "csharp";
  if (/\.css|{[\s\S]*:/.test(text)) return "css";

  return "text";
}

function isPureMath(text = "") {
  const t = text.trim();

  // Contains math symbols / equations
  if (/[=+\-*/^√]/.test(t)) return true;

  // LaTeX patterns
  if (/\\frac|\\sqrt|\\times|\$/.test(t)) return true;

  // Short numeric expressions
  if (/^[0-9x y()+\-*/=.\s]+$/i.test(t)) return true;

  return false;
}

// Add this helper near your other storage helpers
function groupSessionsByDate(sessions) {
  const groups = {
    Today: [],
    Yesterday: [],
    "Last 7 Days": [],
    Older: []
  };

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterday = today - 86400000;
  const lastWeek = today - (7 * 86400000);

  sessions.forEach(s => {
    const ts = s.createdAt;
    if (ts >= today) groups.Today.push(s);
    else if (ts >= yesterday) groups.Yesterday.push(s);
    else if (ts >= lastWeek) groups["Last 7 Days"].push(s);
    else groups.Older.push(s);
  });

  return groups;
}

  
function MessageList({ messages, isLoading }) {
  // 1. DYNAMIC BRANDING: Context-aware icons and colors
  const getSubjectBranding = (text = "") => {
    const p = text.toLowerCase();
    if (/(physics|chemistry|biology|science|lab|acid|base|cell|force|motion)/.test(p)) 
      return { icon: "🧪", color: "#059669", label: "SCIENCE" };
    if (/(math|algebra|geometry|calculus|equation|solve|trigonometry|ratio)/.test(p)) 
      return { icon: "📐", color: "#2563eb", label: "MATHEMATICS" };
    if (/(history|geography|civics|social|economics|political|map|climate)/.test(p)) 
      return { icon: "🌍", color: "#b45309", label: "SOCIAL SCIENCE" };
    if (/(python|javascript|code|program|html|css|sql|algorithm)/.test(p)) 
      return { icon: "💻", color: "#4f46e5", label: "CS / IT" };
    if (/(english|hindi|grammar|essay|poem|literature|unseen)/.test(p)) 
      return { icon: "📚", color: "#db2777", label: "LANGUAGE" };
    return { icon: "📝", color: "#0f172a", label: "EXAMINATION" }; 
  };

  return (
    <div className="messages-wrapper">
      {messages.map((message, idx) => {
        const brand = getSubjectBranding(message.text);
        return (
          <div
            key={message.id || idx}
            className={message.isUser ? "message user-message" : "message ai-message premium-paper-basis"}
          >
            {/* Subject Branding Sidebar (Desktop Only) */}
            {!message.isUser && (
              <div className="premium-side-branding">
                <div className="bot-avatar-badge" style={{ borderColor: brand.color }}>
                  {brand.icon}
                </div>
                <span className="subject-vertical-label" style={{ color: brand.color }}>
                  {brand.label}
                </span>
              </div>
            )}

            <div className="message-content">
              {message.text ? (
                <ReactMarkdown
                  remarkPlugins={[remarkMath]}
                  rehypePlugins={[rehypeKatex, rehypeRaw]}
                  components={{
                    /* SECTION HEADERS */
                    h2: ({ children }) => (
                      <h2 className="paper-section-title">{children}</h2>
                    ),

                    h3: ({ children }) => (
                      <h3 className="paper-sub-section">{children}</h3>
                    ),

                    /* PARAGRAPHS */
                    p: ({ children }) => (
                      <div className="paper-paragraph">{children}</div>
                    ),

                    /* MARKS PILLS */
                    strong: ({ children }) => {
                      const content = String(children);

                      if (/(\[\d+m\]|\(\d+\s?marks?\))/gi.test(content)) {
                        return (
                          <span className="marks-pill">
                            {content}
                          </span>
                        );
                      }

                      return (
                        <strong style={{ fontWeight: 700 }}>
                          {children}
                        </strong>
                      );
                    },

                    /* LISTS */
                    ol: ({ children }) => (
                      <ol className="paper-ol">{children}</ol>
                    ),

                    li: ({ children }) => (
                      <li className="paper-li">{children}</li>
                    ),

                    /* MATH */
                    math: ({ value }) => (
                      <div className="paper-math-block">
                        <AgniTutorMathRenderer
                          value={value}
                          displayMode={true}
                        />
                      </div>
                    ),

                    /* NORMAL CODE DISPLAY */
                    code: ({ children }) => (
                      <code className="inline-code">
                        {children}
                      </code>
                    ),

                    pre: ({ children }) => (
                      <pre className="paper-code-block">
                        {children}
                      </pre>
                    )
                  }}
                >
                  {message.text.replace(
                    /^(SECTION\s[A-E]:?)/gim,
                    "## $1"
                  )}
                </ReactMarkdown>
              ) : (
                !message.isUser && (
                  <div className="loading-dots">
                    Preparing content...
                  </div>
                )
              )}
            </div>
          </div>
        );
      })}

      {/* SKELETON: Matches the Paper Basis layout */}
      {isLoading && (
        <div className="message ai-message loading-state premium-paper-basis">
          <div className="bot-avatar-badge pulsing">✨</div>
          <div className="message-content">
            <div className="skeleton-paper">
              <div className="skeleton-line title-lg pulsing"></div>
              <div className="skeleton-line text-full pulsing"></div>
              <div className="skeleton-line text-full pulsing"></div>
              <div className="skeleton-line text-half pulsing"></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// -------------------- CLIENT CACHE (LIKE REDIS) --------------------

 
 

// ---------- SAFE updated getAcademicAnswer (no image_bytes/type/image_url sent) ----------
/* ... rest of your file unchanged ... */
// ---------- SAFE updated getAcademicAnswer (no image_bytes/type/image_url sent) ----------
const ALLOWED_GENERATION_KEYS = new Set(["temperature", "topP", "topK", "maxOutputTokens"]);

// Normalize role -> "user" or "model"
function normalizeRole(role) {
  if (typeof role !== "string") return "user";
  return role.toLowerCase() === "user" ? "user" : "model";
}

// Build a safe text part for an image.
// mode = "placeholder" -> short placeholder text
// mode = "inline-datauri" -> include full data URI in the text (very long)

function buildImageTextPart(p, placeholderMode = "placeholder") {
  // p may have inlineData, dataUrl, text, image_url, etc.
  if (!p) return { text: "[Image]" };
  if (p.image_url || p.url) {
    return { text: `[ImageURL:${p.image_url || p.url}]` };
  }
  if (p.inlineData || p.dataUrl) {
    if (placeholderMode === "inline-datauri") {
      return { text: `[ImageInline:${String(p.inlineData || p.dataUrl).slice(0, 200)}]` };
    }
    return { text: "[Image: <inline-data>]" };
  }
  if (p.text) return { text: p.text };
  return { text: "[Image]" };
}

function resizeImage(file, maxSize = 768, quality = 0.85) {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) {
      return resolve(file); // non-image fallback
    }

    const img = new Image();
    const canvas = document.createElement("canvas");
    const reader = new FileReader();

    reader.onload = (e) => {
      img.onload = () => {
        let { width, height } = img;

        if (width > height && width > maxSize) {
          height = Math.round((height * maxSize) / width);
          width = maxSize;
        } else if (height > maxSize) {
          width = Math.round((width * maxSize) / height);
          height = maxSize;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) return reject("Image resize failed");
            resolve(blob);
          },
          "image/jpeg",
          quality
        );
      };
      img.src = e.target.result;
    };

    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}


 
/**
 * getAcademicAnswer - safe image handling WITHOUT hosting
 * Handles cached + non-cached backend responses safely
 */
async function getAcademicAnswer(textPrompt, mediaFiles = [], opts = {}) {
  try {
    const cleanPrompt = (textPrompt || "").trim();

    // ===============================
    // EMPTY INPUT GUARD
    // ===============================
    if (!cleanPrompt && (!mediaFiles || mediaFiles.length === 0)) {
      return "⚠️ Please enter a question or upload an image.";
    }

    /* =====================================================
       ✅ LOCAL MATH SHORTCUT (TEXT ONLY)
    ===================================================== */
    if (/^[0-9\s()+\-*/%.]+$/.test(cleanPrompt)) {
      try {
        return `Answer: ${Function(
          `"use strict";return (${cleanPrompt})`
        )()}`;
      } catch {
        // fall through safely
      }
    }

    /* =====================================================
       ✅ IMAGE DETECTION
    ===================================================== */
    const hasImage = Array.isArray(mediaFiles) && mediaFiles.length > 0;

    /* =====================================================
       ✅ PROMPT
    ===================================================== */
    const prompt = hasImage
      ? `The image contains multiple math questions.

TASK:
1. Extract ALL questions from the image.
2. Solve EACH question one by one.
3. Show STEP-BY-STEP working.
4. Number the answers clearly.
5. Do NOT skip any question.
6. Do NOT give only a single number.

FORMAT YOUR RESPONSE EXACTLY LIKE THIS:

Example 1:
Question:
Steps:
Final Answer:

Example 2:
Question:
Steps:
Final Answer:

User request: ${cleanPrompt}`
      : cleanPrompt;

    /* =====================================================
       ✅ FORM DATA
    ===================================================== */
    const formData = new FormData();
    formData.append("prompt", prompt);

    if (Array.isArray(opts.history)) {
      formData.append("history", JSON.stringify(opts.history));
    }

    /* =====================================================
       ✅ IMAGE OPTIMIZATION
    ===================================================== */
    if (mediaFiles?.[0] instanceof File) {
      const optimizedImage = await resizeImage(mediaFiles[0]);
      formData.append("image", optimizedImage, "image.jpg");
    }

    /* =====================================================
       ✅ API CALL
    ===================================================== */
    const resp = await fetch(`${API_BASE}/api/gemini/multimodal`, {
      method: "POST",
      body: formData
    });

    if (!resp.ok) {
      const errText = await resp.text();
      console.error("Gemini backend error:", errText);
      return "⚠️ Unable to get response right now. Please try again.";
    }

    const json = await resp.json();

    /* =====================================================
       ✅ SAFE ANSWER EXTRACTION (CACHE + NON-CACHE)
    ===================================================== */
    let answer =
      json?.answer ??
      json?.candidates?.[0]?.content?.parts
        ?.map((p) => p.text)
        .filter(Boolean)
        .join(" ") ??
      json?.text ??
      json?.output_text ??
      json?.response ??
      "";

    // 🔒 CRITICAL SAFETY: never allow object rendering
    if (typeof answer !== "string") {
      try {
        answer = JSON.stringify(answer);
      } catch {
        answer = String(answer);
      }
    }

    answer = answer.trim();

    if (!answer) {
      return "⚠️ No answer received. Please try again.";
    }

    return answer;

  } catch (err) {
    console.error("🔥 getAcademicAnswer failed:", err);
    console.error("🔥 Stack:", err?.stack);

    return `⚠️ Error: ${err?.message || "Unknown error"}`;
  }
}










/* ---------------- Render formatted text (TEXT ONLY) ---------------- */
// ---------- Safe, escaped render + improved code detection ----------

function escapeHtml(str) {
   if (str == null) return "";

  // temporarily protect allowed tags
  const protectedStr = String(str)
    .replace(/<b>/gi, "%%B_OPEN%%")
    .replace(/<\/b>/gi, "%%B_CLOSE%%")
    .replace(/<strong>/gi, "%%B_OPEN%%")
    .replace(/<\/strong>/gi, "%%B_CLOSE%%");

  // escape everything else
  const escaped = protectedStr
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

  // restore allowed tags
  return escaped
    .replace(/%%B_OPEN%%/g, "<b>")
    .replace(/%%B_CLOSE%%/g, "</b>");
}

 

// 1. IMPROVED: Subject Icon Detection for Premium Feel
function getSubjectIcon(text = "") {
  const p = text.toLowerCase();
  if (/(physics|chemistry|biology|science|reaction|mole|acid|base|salt|cell)/.test(p)) return "🧪";
  if (/(math|algebra|geometry|trigonometry|calculus|equation|calculate|solve)/.test(p)) return "📐";
  if (/(question paper|model paper|exam paper|test paper|marks|section)/.test(p)) return "📝";
  if (/(history|geography|civics|economics|political|social)/.test(p)) return "🌍";
  if (/(python|javascript|code|program|html|css|sql)/.test(p)) return "💻";
  return "✨"; 
}

// 2. NEW: The Skeleton Loader Component
const QuestionPaperSkeleton = () => (
  <div className="skeleton-paper-container">
    <div className="skeleton-header">
      <div className="skeleton-line title-lg"></div>
      <div className="skeleton-line title-sm"></div>
    </div>
    <div className="skeleton-section">
      <div className="skeleton-line section-label"></div>
      {[1, 2].map((i) => (
        <div key={i} className="skeleton-question">
          <div className="skeleton-line text-full"></div>
          <div className="skeleton-line text-half"></div>
        </div>
      ))}
    </div>
  </div>
);

const renderFormattedText = (text) => {
  if (!text) return null;

  const applyPremiumFormatting = (str) => {
    if (!str) return "";

    let out = str;

    /* SECTION / PART HEADERS */

    out = out.replace(
      /^(SECTION\s[A-Z]:?|PART\s[A-Z]:?)/gim,
      (match) => `\n## ${match.replace(/:/g, "").trim()}\n`
    );

    /* MARKS */

    out = out.replace(
      /(\[?\d+\s?marks?\]?|\[\d+m\]|\(\d+\s?marks?\))/gi,
      (match) =>
        `<span className="paper-marks-pill">${match
          .replace(/[\[\]\(\)]/g, "")
          .trim()}</span>`
    );

    /* STEP LABELS */

    out = out.replace(
      /(\**Step\s\d+:?\**|\**Steps:?\**|\**Given:?\**)/gi,
      (match) =>
        `<span className="math-premium-step">${match
          .replace(/[\*:]/g, "")
          .trim()}</span>`
    );

    /* LEGAL HEADERS */

    const headerKeywords = [
      "Legal Basis",
      "Legal Provision", 
      "Applicable Laws",
      "Relevant Law",
      "Relevant Section",
      "Statutory Provision",
      "Legal Position",
      "Legal Analysis",
      "Legal Opinion",
      "Key Points",
      "Important Points",
      "Facts",
      "Issue",
      "Issues",
      "Arguments",
      "Observation",
      "Findings",
      "Remedy",
      "Relief",
      "Procedure",
      "Process",
      "Requirements",
      "Documents Required",
      "Rights",
      "Liabilities",
      "Penalty",
      "Punishment",
      "Compliance",
      "Timeline",
      "Notice",
      "Draft",
      "Recommendation",
      "Conclusion",
      "Summary",
      "Next Steps",
      "Action Required",
      "Important Note"
    ];

    headerKeywords.forEach((kw) => {
      const escapedKw = kw.replace(
        /[.*+?^${}()|[\]\\]/g,
        "\\$&"
      );

      const kwRegex = new RegExp(
        `(\\**${escapedKw}:?\\**)`,
        "gi"
      );

      out = out.replace(kwRegex, (match) => {
        const clean = match
          .replace(/\*/g, "")
          .replace(/:/g, "")
          .trim();

        return `<span className="legal-header-pill">${clean}</span>`;
      });
    });

    /* FINAL ANSWER */

    // FINAL ANSWER
    out = out.replace(
      /\*\*Final Answer:?\*\*/gi,
      () =>
        `<span class="final-answer-pill">Final Answer</span>`
    );

    // ALWAYS ADD DISCLAIMER
    out += `
    <div class="legal-disclaimer">
      DISCLAIMER: IndLawAI provides general legal information only and is not a substitute for professional advice from a qualified advocate.
    </div>
    `;

    return out;
  };

  const processedText = applyPremiumFormatting(
    String(text)
  ).replace(/\n{3,}/g, "\n\n");

  return (
  <div className="aa-ai-body premium-paper-basis">
    <ReactMarkdown
      remarkPlugins={[remarkMath]}
      rehypePlugins={[rehypeKatex, rehypeRaw]}
      components={{
        h2: ({ children }) => (
          <h2 className="paper-section-header">
            {children}
          </h2>
        ),

        h3: ({ children }) => (
          <h3 className="paper-sub-header">
            {children}
          </h3>
        ),

        p: ({ children }) => (
          <div className="premium-p-text">
            {children}
          </div>
        ),

        ol: ({ children }) => (
          <ol className="premium-official-list">
            {children}
          </ol>
        ),

        li: ({ children }) => (
          <li className="premium-official-li">
            {children}
          </li>
        ),

        hr: () => (
          <div className="paper-separator" />
        ),

        math: ({ value }) => (
          <div className="math-block-premium-centered">
            <AgniTutorMathRenderer
              value={value}
              displayMode
            />
          </div>
        ),

        /* OPEN LINKS IN NEW TAB */
        a: ({ href, children }) => (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="legal-link"
          >
            {children}
          </a>
        ),

        code: ({ children }) => (
          <code className="inline-code">
            {children}
          </code>
        ),

        pre: ({ children }) => (
          <pre className="paper-code-block">
            {children}
          </pre>
        )
      }}
    >
      {processedText}
    </ReactMarkdown>
  </div>
);
};



function isCodeMessage(text) {
  if (!text) return false;
  const t = text.trim();
  const lines = t.split("\n").map(l => l.trim()).filter(Boolean);
  if (lines.length < 2) return false;

  // Heuristics: multiple semicolons, common keywords, braces, or function signatures
  const semicolons = (t.match(/;/g) || []).length;
  const braces = (t.match(/[{}]/g) || []).length;
  const hasFunction = /\b(function|def|func|public|private|static|void|int|float|double)\b/.test(t);
  const hasInclude = t.startsWith("#include") || /\b#include\b/.test(t);
  const hasConsole = t.includes("console.log") || t.includes("System.out") || t.includes("printf(");

  if (semicolons >= 2) return true;
  if (braces >= 2 && hasFunction) return true;
  if (hasInclude) return true;
  if (hasConsole) return true;

  return false;
}

function detectCodeLang(text) {
  const t = text || "";
  if (t.includes("#include")) return "c";
  if (/\bSystem\.out\b/.test(t)) return "java";
  if (/\bdef\s+[a-zA-Z0-9_]+\s*\(/.test(t)) return "python";
  if (t.includes("console.log") || /\bexport\s+default\b/.test(t) || /\bimport\s+.+\s+from\b/.test(t)) return "javascript";
  if (/\busing\s+System\b/.test(t) || /\bnamespace\b/.test(t)) return "csharp";
  return "c";
}



export default function AcademicAssistantUI() {
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [showCopyToast, setShowCopyToast] = useState(false);

  useEffect(() => {
    if (!showCopyToast) return undefined;
    const t = setTimeout(() => setShowCopyToast(false), 1100);
    return () => clearTimeout(t);
  }, [showCopyToast]);

  // initialize persisted quiz responses (map of msgId -> selectedIndex)
  const [quizResponses, setQuizResponses] = useState(() => {
    try {
      const raw = localStorage.getItem("agni_quiz_responses");
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      console.warn("failed to load quizResponses", e);
      return {};
    }
  });

  const [quizAnsweredAt, setQuizAnsweredAt] = useState(() => {
    // optional: store timestamp when answered (msgId -> ts)
    try {
      const raw = localStorage.getItem("agni_quiz_answered_at");
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      return {};
    }
  });

  // keep localStorage in sync whenever quizResponses changes
  useEffect(() => {
    try {
      localStorage.setItem("agni_quiz_responses", JSON.stringify(quizResponses));
    } catch (e) {
      console.warn("failed to save quizResponses", e);
    }
  }, [quizResponses]);

  useEffect(() => {
    try {
      localStorage.setItem("agni_quiz_answered_at", JSON.stringify(quizAnsweredAt));
    } catch (e) {
      console.warn("failed to save quizAnsweredAt", e);
    }
  }, [quizAnsweredAt]);

  // helper to record response
  function recordQuizResponse(msgId, selectedIndex) {
    setQuizResponses((prev) => {
      const next = { ...prev, [msgId]: selectedIndex };
      try { localStorage.setItem("agni_quiz_responses", JSON.stringify(next)); } catch(e){}
      return next;
    });
    setQuizAnsweredAt((prev) => {
      const next = { ...prev, [msgId]: Date.now() };
      try { localStorage.setItem("agni_quiz_answered_at", JSON.stringify(next)); } catch(e){}
      return next;
    });
  }

  const [quizLoadingId, setQuizLoadingId] = useState(null);

  const [input, setInput] = useState("");
  const [files, setFiles] = useState([]); // actual File objects to send
  const [previews, setPreviews] = useState([]); // preview objects for UI
  const [isLoading, setIsLoading] = useState(false);
  const [counts, setCounts] = useState({});
  const [isListening, setIsListening] = useState(false);
  const [playingMsgId, setPlayingMsgId] = useState(null);
  
  const [uiMode, setUiMode] = useState("welcome"); 
  const [confirmingDelete, setConfirmingDelete] = useState(null); // Stores the session ID
  // "welcome" | "chat"

  const [showSplash, setShowSplash] = useState(true);
  const [showHistoryMobile, setShowHistoryMobile] = useState(false);

  const [copiedMsgId, setCopiedMsgId] = useState(null);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);

  const [assistantMessage, setAssistantMessage] = useState("");

  const recognitionRef = useRef(null);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const composerAnchorRef = useRef(null);
  const aaCardRef = useRef(null);
  const selectedVoiceRef = useRef(null);
  const prevMessagesLenRef = useRef(0);
  const [leftFooterStyle, setLeftFooterStyle] = useState({});
  const ttsSessionIdRef = useRef(0);

  const isTtsPausedRef = useRef(false);   // tracks whether pause was intentional
  const lastPlayedMsgRef = useRef(null);  // remembers which msgId is active

  const [pendingMathRequest, setPendingMathRequest] = useState(null);
  const [pendingMath, setPendingMath] = useState(null);

  const quizRef = useRef(null);

  const [showScrollDown, setShowScrollDown] = useState(false);

  const [showSocialMenu, setShowSocialMenu] = useState(false);
  const socialMenuRef = useRef(null);

  const [selectedVote, setSelectedVote] = useState({});
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackType, setFeedbackType] = useState("general");
  const [showFeedbackSuccess, setShowFeedbackSuccess] = useState(false);
  const API_BASE = process.env.REACT_APP_API_BASE;

 const handleSubmitFeedback = async () => {
  try {
    const response = await fetch(
      `${API_BASE}/api/feedback`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          category: feedbackType,
          feedback: feedbackText
        })
      }
    );

    if (!response.ok) {
      throw new Error("Feedback submission failed");
    }

    setShowFeedbackSuccess(true);

    setTimeout(() => {
      setShowFeedbackSuccess(false);
    }, 3000);

    setFeedbackText("");
    setFeedbackType("");
    setShowFeedback(false);

  } catch (err) {
    console.error("Feedback Error:", err);
  }
};

  useEffect(() => {
    function handleClickOutside(e) {
      if (
        socialMenuRef.current &&
        !socialMenuRef.current.contains(e.target)
      ) {
        setShowSocialMenu(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);



      useEffect(() => {
      const loadedSessions = loadSessions();
      const loadedCounts = loadCounts();
      setCounts(loadedCounts || {});

      // 🔑 read last selected session
      const lastSessionId = localStorage.getItem("agni:lastSessionId");

      if (loadedSessions.length > 0) {
        setSessions(loadedSessions);

        // ✅ restore last selected chat if exists
        const lastSession =
          lastSessionId &&
          loadedSessions.find((s) => s.id === lastSessionId);

        const activeSession = lastSession || loadedSessions[0];

        setCurrentSessionId(activeSession.id);
        setMessages(activeSession.messages);
        prevMessagesLenRef.current = activeSession.messages.length;
      } else {
        // ✅ no sessions → create new one
        const s = makeSession();
        setSessions([s]);
        setCurrentSessionId(s.id);
        setMessages(s.messages);
        prevMessagesLenRef.current = s.messages.length;
        saveSessions([s]);

        // clear last session reference
        localStorage.removeItem("agni:lastSessionId");
      }

      function pickVoice() {
        const voices = window.speechSynthesis.getVoices();
        const v = selectFemaleVoice(voices);
        if (v) selectedVoiceRef.current = v;
      }

      pickVoice();
      window.speechSynthesis.onvoiceschanged = pickVoice;

      const t = setTimeout(() => setShowSplash(false), 1200);
      return () => {
        clearTimeout(t);
        window.speechSynthesis.onvoiceschanged = null;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


  useEffect(() => {
  if (messages && messages.length > 0) {
    setUiMode("chat");
  }
}, [messages]);



  // Persist helpers (unchanged)
  function persistSessions(updated) {
    setSessions(updated);
    saveSessions(updated);
  }
  function persistCountsState(obj) {
    setCounts(obj);
    saveCounts(obj);
  }
  function persistCurrentMessages(updatedMessages, sessionId = currentSessionId) {
    const updated = sessions.map((s) => (s.id === sessionId ? { ...s, messages: updatedMessages } : s));
    persistSessions(updated);
    if (sessionId === currentSessionId) setMessages(updatedMessages);
  }

  function startChatWithMode(modeLabel) {
    setUiMode("chat");
    setInput(modeLabel + ": ");
  }

  function appendToSession(msg, sessionId = currentSessionId) {
    setSessions((prevSessions) => {
      const updated = prevSessions.map((s) =>
        s.id === sessionId
          ? { ...s, messages: [...s.messages, msg] }
          : s
      );

      saveSessions(updated);

      // sync chat UI immediately
      if (sessionId === currentSessionId) {
        const current = updated.find(s => s.id === sessionId);
        if (current) setMessages(current.messages);
      }

      return updated;
    });
  }



  function newSessions() {
    const s = makeSession();
    const updated = [s, ...sessions];   // ✅ add new session at TOP
    persistSessions(updated);
    setCurrentSessionId(s.id);
    setMessages(s.messages);
    setInput("");
    setFiles([]);
    setPreviews([]);
  }
  
  function openSession(id) {
    const s = sessions.find((x) => x.id === id);
    if (!s) return;

    // ✅ remember selected session
    localStorage.setItem("agni:lastSessionId", id);

    setCurrentSessionId(id);
    setMessages(s.messages);

    // reset composer state (keep this as you had)
    setInput("");
    setFiles([]);
    setPreviews([]);

    // close mobile history
    setShowHistoryMobile(false);
  }

  function deleteSession(id) {
    const updated = sessions.filter((s) => s.id !== id);
    persistSessions(updated);
    if (id === currentSessionId) {
      if (updated.length > 0) {
        const last = updated[updated.length - 1];
        setCurrentSessionId(last.id);
        setMessages(last.messages);
      } else {
        const s = makeSession();
        persistSessions([s]);
        setCurrentSessionId(s.id);
        setMessages(s.messages);
      }
    }
  }

  function openDeleteAllModal() { setShowDeleteAllModal(true); }
  function closeDeleteAllModal() { setShowDeleteAllModal(false); }

  function confirmDeleteAll() {
    try {
      const s = makeSession();
      persistSessions([s]);
      setCurrentSessionId(s.id);
      setMessages(s.messages);
      persistCountsState({});
    } catch (e) {
      console.warn("confirmDeleteAll failed", e);
    } finally {
      setShowDeleteAllModal(false);
    }
  }

  function DangerModal({
  open,
  title = "Confirm",
  description = "",
  confirmLabel = "Delete",
  cancelLabel = "Cancel",
  onConfirm,
  onCancel,
  children
}) {
  const css = `
    .aa-modal-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(2,6,23,0.45);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 14000;
      padding: 20px;
    }

    .aa-modal {
      width: 100%;
      max-width: 520px;
      background: #fff;
      border-radius: 16px;
      box-shadow: 0 18px 50px rgba(2,6,23,0.2);
      padding: 20px;
      color: #0f172a;
      font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, Arial;
    }

    .aa-modal h3 {
      margin: 0 0 8px;
      font-size: 20px;
      font-weight: 700;
    }

    .aa-modal p {
      margin: 0 0 16px;
      color: #64748b;
      line-height: 1.6;
    }

    .aa-modal-content {
      margin-top: 12px;
    }

    .aa-modal .footer {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      margin-top: 20px;
    }

    .aa-modal .btn {
      padding: 10px 16px;
      border-radius: 10px;
      border: 1px solid transparent;
      cursor: pointer;
      font-size: 14px;
      font-weight: 600;
    }

    .aa-modal .btn.cancel {
      background: #fff;
      border-color: #e5e7eb;
      color: #374151;
    }

    .aa-modal .btn.danger {
      background: #dc2626;
      color: #fff;
      border-color: #dc2626;
    }
  `;

  if (!open) return null;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: css }} />

      <div
        className="aa-modal-backdrop"
        role="dialog"
        aria-modal="true"
      >
        <div className="aa-modal">
          <h3>{title}</h3>

          {description && <p>{description}</p>}

          <div className="aa-modal-content">
            {children}
          </div>

          <div className="footer">
            <button
              className="btn cancel"
              onClick={onCancel}
            >
              {cancelLabel}
            </button>

            <button
              className="btn danger"
              onClick={onConfirm}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// Local quiz generator (NO external API) — sanitized output (no HTML)
async function buildQuizFromAnswer(answerText, userQuestion = "") {
  if (!answerText || !answerText.trim()) return null;
  const raw = answerText.replace(/\s+/g, " ").trim();

  // --- helpers ---
  function stripHtml(input) {
    if (!input) return "";
    // remove tags
    const stripped = input.replace(/<[^>]*>/g, " ");
    // collapse whitespace
    return stripped.replace(/\s+/g, " ").trim();
  }

  function decodeHtmlEntities(str) {
    if (!str) return "";
    // In browser, use a textarea to decode entities
    try {
      const txt = document.createElement("textarea");
      txt.innerHTML = str;
      return txt.value;
    } catch (e) {
      // fallback: basic replacements
      return str
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'");
    }
  }

  function clean(s) {
    return decodeHtmlEntities(stripHtml(String(s || "") || "")).trim();
  }

  function pickBestSentence(t) {
    const sents = t
      .split(/(?<=[.!?])\s+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 8);
    if (!sents.length) return t.slice(0, 200);
    const score = (s) =>
      (/\b(is|are|means|refers to|describes|causes|results|called|defined)\b/i.test(s) ? 2 : 0) +
      (/\d/.test(s) ? 2 : 0) +
      Math.min(3, s.split(" ").length / 8);
    sents.sort((a, b) => score(b) - score(a));
    return sents[0];
  }

  function extractTopic(sentence) {
    const m = sentence.match(/^(.*?)\s+(?:is|are|means|refers to|describes|called|defined)\b/i);
    if (m && m[1]) return m[1].trim();
    return sentence.split(/\s+/).slice(0, 2).join(" ");
  }

  function shuffle(a) {
    const arr = [...a];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  function escapeRegExp(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }


  function makeDistractors(correct, sentence, topic) {
    const set = new Set();

    if (correct.length < 140 && !/not|no|except/i.test(correct)) {
      set.add("Not " + correct);
    }

    const numMatch = correct.match(/-?\d+(\.\d+)?/);
    if (numMatch) {
      const n = parseFloat(numMatch[0]);
      if (!Number.isNaN(n)) {
        set.add(String(n + 1));
        set.add(String(n - 1));
        set.add(String(Math.round(n * 1.1)));
      }
    }

    if (topic && !new RegExp(escapeRegExp(topic), "i").test(correct)) {
      set.add(`${topic} (different meaning)`);
    }

    const tokens = sentence
      .replace(/[^\w\s]/g, " ")
      .split(/\s+/)
      .filter((t) => t.length > 3 && !/^(the|and|that|which|with|for|from|this)$/i.test(t));

    for (let i = 0; i < Math.min(4, tokens.length) && set.size < 6; i++) {
      const tok = tokens[i];
      set.add(`A statement about ${tok}`);
    }

    const fillers = [
      "A related but incorrect statement.",
      "An unrelated idea not discussed above.",
      "A common misconception on this topic.",
      "A different but incorrect option.",
    ];
    let idx = 0;
    while (set.size < 3 && idx < fillers.length) {
      set.add(fillers[idx++]);
    }

    const final = Array.from(set)
      .map((d) => clean(d))
      .filter((d) => d && d.toLowerCase() !== clean(correct).toLowerCase())
      .slice(0, 3);

    while (final.length < 3) final.push("A different but incorrect option.");
    return final;
  }

  // --- main logic ---
  // sanitize raw answer first (strip HTML & decode entities)
  const safeRaw = clean(raw);

  const best = pickBestSentence(safeRaw);
  let correct = best;

  const afterIs = best.match(/\b(?:is|are|means|refers to|describes|called|defined)\b\s*(.*)/i);
  if (afterIs && afterIs[1] && afterIs[1].trim().length > 6) {
    correct = afterIs[1].trim();
  }

  if (correct.length > 140) {
    const cut = correct.slice(0, 140);
    const lastSpace = cut.lastIndexOf(" ");
    correct = (lastSpace > 40 ? cut.slice(0, lastSpace) : cut).trim() + "...";
  }

  // guard: if correct equals the user's question or is just the same short phrase, avoid duplication
  const cleanedUserQ = clean(userQuestion || "");
  if (cleanedUserQ && cleanedUserQ.length && cleanedUserQ.toLowerCase() === correct.toLowerCase()) {
    // fallback to using the best sentence as correct (already cleaned)
    correct = clean(best);
  }

  const topic = clean(extractTopic(best)) || "the concept above";

  // <-- FIXED: use backticks for template strings
  const templates = [
    `Which of the following BEST describes ${topic}?`,
    `Based on the explanation above, which option is MOST accurate?`,
    `Which statement correctly represents ${topic}?`,
    `What is the main idea from the explanation above?`,
  ];

  let questionTemplate =
    templates.find((t) => !(cleanedUserQ && t.toLowerCase().includes(cleanedUserQ.toLowerCase()))) || templates[0];
  let question = clean(questionTemplate);

  const distractors = makeDistractors(correct, best, topic);
  const options = shuffle([clean(correct), ...distractors]);

  // ensure correctIndex exists
  let correctIndex = options.findIndex((o) => o === clean(correct));
  if (correctIndex === -1) {
    options[0] = clean(correct);
    correctIndex = 0;
  }

  // ensure exactly 4 options
  const finalOptions = options.slice(0, 4);
  while (finalOptions.length < 4) finalOptions.push("A different but incorrect option.");

  // final sanity: if question equals user's question, adjust
  if (cleanedUserQ && question.trim().toLowerCase() === cleanedUserQ.trim().toLowerCase()) {
    question = "Pick the correct explanation based on the answer above.";
  }

  return {
    question: clean(question),
    options: finalOptions.map((o) => clean(o)),
    correctIndex: finalOptions.findIndex((o) => o === clean(correct)),
  };
}

// --- Small shared helpers (place these immediately after imports) ---
// Voice selection helper used by TTS
function selectFemaleVoice(voices) {
  if (!voices || voices.length === 0) return null;

  const preferredVoices = [
    "Google UK English Female",
    "Google US English Female",
    "Samantha",
    "Victoria",
    "Joanna",
    "Kendra",
    "Ivy"
  ];

  // exact match first
  for (const name of preferredVoices) {
    const v = voices.find((vc) => vc && vc.name === name);
    if (v) return v;
  }

  // look for 'female' in name
  const femaleByKeyword = voices.find((vc) => vc && typeof vc.name === "string" && vc.name.toLowerCase().includes("female"));
  if (femaleByKeyword) return femaleByKeyword;

  // fallback to first English voice
  const en = voices.find((vc) => vc && typeof vc.lang === "string" && vc.lang.startsWith("en"));
  if (en) return en;

  // final fallback
  return voices[0] || null;
}

// Safe image URL sanitizer for thumbnails/full images
function sanitizeImageUrl(url, prompt = "") {
  try {
    if (!url) {
      const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='400' height='200'><rect width='100%' height='100%' fill='#f3f4f6'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='18' fill='#111827'>No image</text></svg>`;
      return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    }
    const s = String(url).trim();
    if (s.startsWith("data:")) return s;
    if (s.startsWith("//")) return window.location.protocol + s;
    if (/^[\w.-]+\//.test(s)) return "https://" + s;
    if (s.startsWith("http://") || s.startsWith("https://")) return s;
    return s;
  } catch (e) {
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='400' height='200'><rect width='100%' height='100%' fill='#f3f4f6'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='18' fill='#111827'>No image</text></svg>`;
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  }
}




async function handleCopyText(text, setShowCopyToast) {
  try {
    if (!text) return false;

    // 1. CLEANING PHASE: Convert LaTeX/Markdown to human-readable text
    const cleaned = String(text)
      .replace(/\[GeneratedImageURL:[^\]]*\]/g, "") // Remove AI image tags
      .replace(/<Diagram:[^>]*>/gi, "")              // Remove diagram tags
      .replace(/Caption:\s*.*$/gim, "")             // Remove captions
      
      // Remove LaTeX dollar signs ($ and $$)
      .replace(/\$\$(.*?)\$\$/g, '$1') 
      .replace(/\$(.*?)\$/g, '$1')
      
      // Remove Markdown bold stars (**)
      .replace(/\*\*/g, '')

      // 2. MATH FORMATTING: Convert LaTeX code to clean symbols
      .replace(/\\pi/g, 'π')                        // \pi -> π
      .replace(/\\sqrt\{([^}]*)\}/g, '√($1)')       // \sqrt{x} -> √(x)
      .replace(/\\frac\{([^}]*)\}\{([^}]*)\}/g, '($1/$2)') // \frac{a}{b} -> (a/b)
      .replace(/\\times/g, '×')                     // \times -> ×
      .replace(/\\cdot/g, '·')                      // \cdot -> ·
      .replace(/\\left\(|\\right\)/g, (m) => m === '\\left\(' ? '(' : ')') // Fix brackets
      .replace(/\\left\[|\\right\]/g, (m) => m === '\\left\[' ? '[' : ']')
      .replace(/_\{([^}]*)\}/g, '_$1')              // V_{cone} -> V_cone
      .replace(/\^\{([^}]*)\}/g, '^$1')              // r^{2} -> r^2
      .trim();

    if (!cleaned) return false;

    // 3. COPY TO CLIPBOARD
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(cleaned);
    } else {
      // Fallback for older browsers
      const ta = document.createElement("textarea");
      ta.value = cleaned;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (!ok) throw new Error("copy failed");
    }

    // 4. Show success toast
    if (typeof setShowCopyToast === "function") setShowCopyToast(true);
    return true;

  } catch (err) {
    console.warn("handleCopyText failed", err);
    if (typeof setShowCopyToast === "function") setShowCopyToast(false);
    return false;
  }
}

// speakText (robust + abortable)
async function speakText(text, msgId) {
  if (!("speechSynthesis" in window)) {
    alert("Text-to-speech not supported in this browser.");
    return;
  }

  // If there's a paused speech session, resume it instead of restarting.
  try {
    if (window.speechSynthesis && window.speechSynthesis.paused) {
      try {
        window.speechSynthesis.resume();
        setPlayingMsgId(msgId);
      } catch (e) {
        console.warn("speakText: resume failed", e);
      }
      return;
    }
  } catch (e) {
    console.warn("speakText: paused check failed", e);
  }

  // create a new local session id for this speak call
  const mySession = (ttsSessionIdRef.current || 0) + 1;
  ttsSessionIdRef.current = mySession;

  try {
    const cleanText = getReadableText(text);
    if (!cleanText) {
      console.info("speakText: nothing to speak after cleaning.");
      return;
    }

    const voices = await ensureVoicesLoaded(1200);
    if (voices && voices.length > 0 && !selectedVoiceRef.current) {
      selectedVoiceRef.current = selectFemaleVoice(voices);
    }

    // re-resolve selected voice to a current instance if possible
    if (selectedVoiceRef.current && voices && voices.length > 0) {
      const matched = voices.find((v) => v.name === selectedVoiceRef.current.name);
      if (matched) selectedVoiceRef.current = matched;
    }

    // Only cancel previous speech if something is actively speaking (not paused).
    try {
      if (window.speechSynthesis && window.speechSynthesis.speaking && !window.speechSynthesis.paused) {
        try { window.speechSynthesis.cancel(); } catch (e) { console.warn("TTS cancel prior", e); }
      }
    } catch (e) {
      console.warn("speakText: cancel check failed", e);
    }

    // chunk logic (same as before)
    const MAX_LEN = 2400;
    const toSpeakParts = [];
    if (cleanText.length > MAX_LEN) {
      const sentences = cleanText.match(/[^\.!\?]+[\.!\?]*/g) || [cleanText];
      let current = "";
      sentences.forEach((s) => {
        if ((current + s).length > MAX_LEN) {
          if (current) { toSpeakParts.push(current.trim()); current = s; }
          else { toSpeakParts.push(s.trim()); current = ""; }
        } else current += s;
      });
      if (current) toSpeakParts.push(current.trim());
    } else {
      toSpeakParts.push(cleanText);
    }

    console.info("speakText: speaking", { msgId, parts: toSpeakParts.length, voice: selectedVoiceRef.current?.name });
    setPlayingMsgId(msgId);

    // Adjust these rates to taste (lower = slower)
    const DEFAULT_RATE = 0.80;   // default speaking rate (slower)
    const LANG_INDIC_RATE = 0.82; // slightly different rate for Indic languages

    for (const part of toSpeakParts) {
      // check whether we've been asked to stop since this loop started
      if (ttsSessionIdRef.current !== mySession) {
        console.info("speakText: aborted before starting next part", { mySession, current: ttsSessionIdRef.current });
        break;
      }

      // wait for this utterance to finish or for abort
      const finished = await new Promise((resolvePart) => {
        let resolvedFlag = false;
        const u = new SpeechSynthesisUtterance(part);

        // --- UPDATED BLOCK: language detection and voice selection ---
        // detect common Indic script blocks; fall back to en-IN
        let detectedLang = null;
        try {
          if (/[\u0B80-\u0BFF]/.test(part)) {
            // Tamil Unicode block
            detectedLang = "ta-IN";
          } else if (/[\u0900-\u097F]/.test(part)) {
            // Devanagari (Hindi, Marathi, etc.)
            detectedLang = "hi-IN";
          } else if (/[\u0980-\u09FF]/.test(part)) {
            // Bengali
            detectedLang = "bn-IN";
          } else if (/[\u0C00-\u0C7F]/.test(part)) {
            // Telugu
            detectedLang = "te-IN";
          } else if (/[\u0C80-\u0CFF]/.test(part)) {
            // Kannada
            detectedLang = "kn-IN";
          } else if (/[\u0D00-\u0D7F]/.test(part)) {
            // Malayalam
            detectedLang = "ml-IN";
          } else {
            detectedLang = null;
          }
        } catch (e) {
          console.warn("speakText: lang detect failed", e);
          detectedLang = null;
        }

        // get available voices
        let curVoices = [];
        try {
          curVoices = window.speechSynthesis.getVoices() || [];
        } catch (e) {
          console.warn("speakText: getVoices() failed", e);
        }

        try {
          const nameMatch = selectedVoiceRef.current
            ? curVoices.find((v) => v.name === selectedVoiceRef.current.name)
            : null;

          const langExact = detectedLang ? curVoices.find((v) => v.lang === detectedLang) : null;
          const langStarts = detectedLang ? curVoices.find((v) => v.lang && v.lang.startsWith(detectedLang.split("-")[0])) : null;

          if (nameMatch) {
            u.voice = nameMatch;
          } else if (langExact) {
            u.voice = langExact;
          } else if (langStarts) {
            u.voice = langStarts;
          } else if (selectedVoiceRef.current) {
            const loose = curVoices.find((v) => v.name === selectedVoiceRef.current.name);
            if (loose) u.voice = loose;
            else if (curVoices.length) u.voice = curVoices[0];
          } else if (curVoices.length) {
            u.voice = curVoices[0];
          }
        } catch (e) {
          console.warn("speakText: voice selection failed", e);
        }

        // set utterance language: prefer detectedLang, else derive from voice, else en-IN
        u.lang = detectedLang || u.voice?.lang || "en-IN";

        // Set rate based on detected language (lower = slower)
        u.rate = detectedLang ? LANG_INDIC_RATE : DEFAULT_RATE;
        u.pitch = 1.05;
        u.volume = 1.0;
        // --- END UPDATED BLOCK ---

        // Safety poller for abort
        const pollInterval = 120;
        const poller = setInterval(() => {
          if (ttsSessionIdRef.current !== mySession) {
            try { window.speechSynthesis.cancel(); } catch (e) {}
            clearInterval(poller);
            if (!resolvedFlag) { resolvedFlag = true; resolvePart({ ok: false, reason: "aborted" }); }
          }
        }, pollInterval);

        u.onend = () => {
          try { clearInterval(poller); } catch (e) {}
          if (!resolvedFlag) { resolvedFlag = true; resolvePart({ ok: true, reason: "end" }); }
        };
        u.onerror = (ev) => {
          try { clearInterval(poller); } catch (e) {}
          if (!resolvedFlag) { resolvedFlag = true; console.warn("TTS part error", ev); resolvePart({ ok: false, reason: "error" }); }
        };

        try {
          window.speechSynthesis.speak(u);
        } catch (e) {
          try { clearInterval(poller); } catch (ee) {}
          if (!resolvedFlag) { resolvedFlag = true; console.warn("speakText: speak() threw", e); resolvePart({ ok: false, reason: "throw" }); }
        }
      });

      // if aborted, break
      if (ttsSessionIdRef.current !== mySession) {
        console.info("speakText: detected abort after part", { mySession, current: ttsSessionIdRef.current });
        break;
      }
    }
  } catch (err) {
    console.warn("speakText unexpected error", err);
  } finally {
    // If this session is still the active session, clear playing state
    if (ttsSessionIdRef.current === mySession) setPlayingMsgId(null);
  }
}


// Pause current speech (keeps the current queue so resume() will continue)
function pauseSpeaking() {
  try {
    if (!("speechSynthesis" in window)) return;
    if (window.speechSynthesis.speaking && !window.speechSynthesis.paused) {
      window.speechSynthesis.pause();
      console.info("TTS paused");
      // keep playingMsgId if you want to show paused state, or set to null to show 'not playing'
      setPlayingMsgId(null);
    }
  } catch (e) { console.warn("pauseSpeaking failed", e); }
}

function resumeSpeaking(msgId) {
  try {
    if (!("speechSynthesis" in window)) return;
    if (window.speechSynthesis.paused) {
      window.speechSynthesis.resume();
      console.info("TTS resumed");
      setPlayingMsgId(msgId || playingMsgId);
    }
  } catch (e) { console.warn("resumeSpeaking failed", e); }
}


// hardStop true = cancel and forget position; hardStop false = pause (soft stop)
function stopSpeaking(hardStop = true) {
  try {
    if (!("speechSynthesis" in window)) return;
    const synth = window.speechSynthesis;
    if (hardStop) {
      console.log("stopSpeaking (hard) called — stack:", new Error().stack);
      ttsSessionIdRef.current = (ttsSessionIdRef.current || 0) + 1;
      try { synth.cancel(); } catch (e) { console.warn("stopSpeaking: cancel failed", e); }
      setPlayingMsgId(null);
      lastPlayedMsgRef.current = null;
      isTtsPausedRef.current = false;
      console.info("TTS stopped (hard)");
    } else {
      pauseSpeaking();
    }
  } catch (e) {
    console.warn("stopSpeaking unexpected", e);
  }
}



// Clean and extract speakable text from AI message text
function getReadableText(raw) {
  try {
    if (!raw) return "";
    let s = String(raw);

    // Remove bracketed generated image URLs: [GeneratedImageURL:...]
    s = s.replace(/\[GeneratedImageURL:[^\]]*\]/g, " ");

    // Remove explicit caption lines: "Caption: ..." (single-line)
    s = s.replace(/^\s*Caption:\s*.*$/gim, " ");

    // Remove the diagram tag line if present: <Diagram: ...>
    s = s.replace(/<Diagram:[^>]*>/gi, " ");

    // Remove markdown-style titles like "### Title" or "# Title"
    s = s.replace(/^\s{0,3}#{1,6}\s.*$/gim, " ");

    // Remove any leftover HTML tags (if any)
    s = s.replace(/<\/?[^>]+(>|$)/g, " ");

    // Collapse multi-newlines and trim
    s = s.replace(/\r\n/g, "\n").replace(/\n{2,}/g, "\n\n").trim();

    // Remove any auto symbols (global)
    s = typeof removeAutoSymbols === "function" ? removeAutoSymbols(s) : s;

    // If text is still very long, limit length for reliability
    if (s.length > 4000) s = s.slice(0, 4000) + " ...";

    return s;
  } catch (e) {
    console.warn("getReadableText failed", e);
    return typeof removeAutoSymbols === "function" ? removeAutoSymbols(String(raw || "")) : String(raw || "");
  }
}

// Wait for voices to populate (small timeout)
function ensureVoicesLoaded(timeoutMs = 1000) {
  return new Promise((resolve) => {
    try {
      const voicesNow = window.speechSynthesis.getVoices();
      if (voicesNow && voicesNow.length > 0) return resolve(voicesNow);
      let resolved = false;
      const handler = () => {
        if (resolved) return;
        const vs = window.speechSynthesis.getVoices();
        if (vs && vs.length > 0) {
          resolved = true;
          if (typeof window.speechSynthesis.removeEventListener === "function") {
            window.speechSynthesis.removeEventListener("voiceschanged", handler);
          } else {
            window.speechSynthesis.onvoiceschanged = null;
          }
          resolve(vs);
        }
      };
      if (typeof window.speechSynthesis.addEventListener === "function") {
        window.speechSynthesis.addEventListener("voiceschanged", handler);
      } else {
        window.speechSynthesis.onvoiceschanged = handler;
      }
      // fallback timeout
      setTimeout(() => {
        if (!resolved) {
          if (typeof window.speechSynthesis.removeEventListener === "function") {
            window.speechSynthesis.removeEventListener("voiceschanged", handler);
          } else {
            window.speechSynthesis.onvoiceschanged = null;
          }
          resolve(window.speechSynthesis.getVoices() || []);
        }
      }, timeoutMs);
    } catch (e) {
      resolve([]);
    }
  });
}


/* Tiny CopyToast component: call setShowCopyToast(true) to display for ~1.1s */
function CopyToast({ visible }) {
  const css = `
    .aa-copy-toast {
      position: fixed;
      right: 22px;
      bottom: 86px;
      background: rgba(17,24,39,0.95);
      color: #fff;
      padding: 8px 12px;
      border-radius: 12px;
      font-size: 13px;
      box-shadow: 0 6px 18px rgba(2,6,23,0.12);
      transform: translateY(8px);
      opacity: 0;
      pointer-events: none;
      transition: opacity 220ms ease, transform 220ms ease;
      z-index: 99999;
    }
    .aa-copy-toast.show { opacity: 1; transform: translateY(0); pointer-events: auto; }
  `;
  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: css }} />
      <div role="status" aria-live="polite" className={`aa-copy-toast ${visible ? "show" : ""}`}>
        Copied to clipboard ✓
      </div>
    </>
  );
}

function applyDevCSP() {
  try {
    const connectHosts = [
      "'self'",
      "http://127.0.0.1:5000",
      "http://localhost:3000",
      "http://localhost:5001",
      "https://generativelanguage.googleapis.com",
      "https://indlawai.el.r.appspot.com",
      "https://indlawai.com"
    ].join(" ");

    const imgHosts = [
      "'self'",
      "data:",
      "blob:",
      "https://picsum.photos",
      "https://fastly.picsum.photos"
    ].join(" ");

    const csp = [
      `default-src 'self'`,
      `connect-src ${connectHosts}`,
      `img-src ${imgHosts}`,
      `script-src 'self' 'unsafe-inline'`,
      `style-src 'self' 'unsafe-inline'`,
      `font-src 'self' data:`,
      `object-src 'none'`,
      `frame-ancestors 'none'`
    ].join("; ");

    let meta = document.querySelector(
      'meta[http-equiv="Content-Security-Policy"]'
    );

    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute(
        "http-equiv",
        "Content-Security-Policy"
      );
      document.head.appendChild(meta);
    }

    meta.setAttribute("content", csp);
  } catch (e) {
    console.warn("applyDevCSP failed", e);
  }
}

/* ---------------- Premium UI Formatter ---------------- */
const formatPremiumResponse = (text) => {
  if (!text) return "";

  let formatted = text;

  // 1. Transform "Step X:" into a styled badge
  formatted = formatted.replace(
    /(Step\s\d+:)/gi, 
    '<span className="step-badge">$1</span>'
  );

  // 2. Transform "Final Answer:" into a highlighted result box
  // This looks for the "Final Answer" line and wraps it
  formatted = formatted.replace(
    /(Final\sAnswer:.*)/gi,
    '<div className="final-result-container">$1</div>'
  );

  // 3. Add a visual divider before major sections (Solution Overview/Numerical Analysis)
  formatted = formatted.replace(
    /(###\s\*\*.*?\*\*)/g,
    '<div className="section-divider"></div>$1'
  );

  return formatted;
};

/* ---------- Scrolling & UI helpers (unchanged but hardened) ---------- */
function scrollAIMessageTopAboveComposer(msgEl) {
  if (!msgEl) return;
  const container = scrollRef.current;
  if (!container) return;
  const composerH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--composer-h")) || 112;
  const SMALL_OFFSET = 12;
  try {
    const visibleHeight = container.clientHeight;
    const msgTopInContainer = msgEl.offsetTop;
    const targetY = Math.max(0, visibleHeight - composerH - SMALL_OFFSET);
    const desiredScrollTop = msgTopInContainer - targetY;
    const maxScrollTop = Math.max(0, container.scrollHeight - visibleHeight);
    const finalScrollTop = Math.max(0, Math.min(maxScrollTop, desiredScrollTop));
    container.scrollTo ? container.scrollTo({ top: finalScrollTop, behavior: "smooth" }) : (container.scrollTop = finalScrollTop);
  } catch (e) {
    try { container.scrollTop = Math.max(0, msgEl.offsetTop - 100); } catch {}
  }
}
function scrollToBottom() {
  const container = scrollRef.current;
  if (!container) return;
  try {
    container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
  } catch {
    container.scrollTop = container.scrollHeight;
  }
}
function scrollAfterMedia(row, isUser) {
  if (!row) return;
  const imgs = Array.from(row.querySelectorAll("img"));
  if (imgs.length === 0) {
    if (isUser) scrollToBottom();
    else scrollAIMessageTopAboveComposer(row);
    return;
  }
  let loaded = 0;
  const done = () => {
    loaded++;
    if (loaded >= imgs.length) {
      if (isUser) scrollToBottom();
      else scrollAIMessageTopAboveComposer(row);
    }
  };
  imgs.forEach((img) => {
    if (img.complete) done();
    else {
      img.addEventListener("load", done, { once: true });
      img.addEventListener("error", done, { once: true });
    }
  });
}

useEffect(() => {
  const container = scrollRef.current;
  if (!container) return;
  const prevLen = prevMessagesLenRef.current;
  const curLen = messages.length;
  prevMessagesLenRef.current = curLen;
  if (curLen === 0) return;
  const rows = container.querySelectorAll(".aa-message-row");
  const lastRow = rows[rows.length - 1];
  if (!lastRow) {
    scrollToBottom();
    return;
  }
  const lastMsg = messages[curLen - 1];
  if (lastMsg && lastMsg.isUser) {
    requestAnimationFrame(() => { scrollAfterMedia(lastRow, true); });
    return;
  }
  const DELAY = 40;
  const t = setTimeout(() => { scrollAfterMedia(lastRow, false); }, DELAY);
  return () => clearTimeout(t);
}, [messages]);

useEffect(() => {
  return () => {
    try {
      previews.forEach((p) => { if (p.url) URL.revokeObjectURL(p.url); });
    } catch {}
  };
}, [previews]);

/* ---------- Attachments ---------- */
const createPreviewURL = (file) => {
  try { return URL.createObjectURL(file); } catch { return ""; }
};



function handleFileChange(e) {
  const chosen = Array.from(e.target.files || []);
  if (!chosen.length) return;

  const newFiles = chosen.map((f) => ({
    file: f,
    name: f.name,
    type: f.type,
    size: f.size,
    previewUrl: URL.createObjectURL(f) // UI only
  }));

  setFiles((prev) => [...prev, ...newFiles.map(f => f.file)]);

  setPreviews((prev) => [
    ...prev,
    ...newFiles.map(f => ({
      name: f.name,
      size: f.size,
      url: f.previewUrl
    }))
  ]);

  if (fileInputRef.current) fileInputRef.current.value = "";
}




function removePreview(name, size) {
  setFiles((f) => f.filter((x) => !(x.name === name && x.size === size)));
  setPreviews((p) => {
    const removed = p.find((x) => x.name === name && x.size === size);
    if (removed && removed.url) {
      try { URL.revokeObjectURL(removed.url); } catch (e) {}
    }
    return p.filter((x) => !(x.name === name && x.size === size));
  });
}

/* ---------- Likes ---------- */
function handleVote(msgId, val) {
  const countsCurr = { ...counts };
  if (!countsCurr[msgId]) countsCurr[msgId] = { like: 0, dislike: 0 };
  if (val === 1) countsCurr[msgId].like = (countsCurr[msgId].like || 0) + 1;
  if (val === -1) countsCurr[msgId].dislike = (countsCurr[msgId].dislike || 0) + 1;
  persistCountsState(countsCurr);
}

function handleWhatsAppShare(rawAnswerText) {
  if (!rawAnswerText) return;

  // Optimized helper to clean LaTeX and Markdown specifically for WhatsApp
  const cleanForShare = (str) => {
    if (!str) return "";
    return String(str)
      .replace(/\[GeneratedImageURL:[^\]]*\]/g, "") // Remove AI image tags
      .replace(/<Diagram:[^>]*>/gi, "")              // Remove diagram tags
      .replace(/Caption:\s*.*$/gim, "")             // Remove captions
      .replace(/<\/?[^>]+(>|$)/g, "")               // Remove HTML tags
      
      // 1. Remove LaTeX markers
      .replace(/\$\$(.*?)\$\$/g, '$1') 
      .replace(/\$(.*?)\$/g, '$1')
      
      // 2. Remove Markdown bold stars
      .replace(/\*\*/g, '')

      // 3. MATH FORMATTING: Convert LaTeX code to clean symbols for WhatsApp
      .replace(/\\pi/g, 'π')                        // \pi -> π
      .replace(/\\sqrt\{([^}]*)\}/g, '√($1)')       // \sqrt{x} -> √(x)
      .replace(/\\frac\{([^}]*)\}\{([^}]*)\}/g, '($1/$2)') // \frac{a}{b} -> (a/b)
      .replace(/\\times/g, '×')                     // \times -> ×
      .replace(/\\cdot/g, '·')                      // \cdot -> ·
      .replace(/\\left\(|\\right\)/g, (m) => m === '\\left\(' ? '(' : ')')
      .replace(/_\{([^}]*)\}/g, '_$1')              // V_{cone} -> V_cone
      .replace(/\^\{([^}]*)\}/g, '^$1')              // r^{2} -> r^2
      .trim();
  };

  const cleanedAnswer = cleanForShare(rawAnswerText);
  if (!cleanedAnswer) return;

  // Find the question and clean it too
  const currentSession = sessions.find((s) => s.id === currentSessionId);
  const rawQuestion = lastUserQuestionFull(currentSession);
  const cleanedQuestion = cleanForShare(rawQuestion);

  let combined;
  if (cleanedQuestion) {
    // We keep the * asterisks here because WhatsApp uses them to make text BOLD
    combined =
      "*Question:*\n" +
      cleanedQuestion +
      "\n\n*Answer:*\n" +
      cleanedAnswer;
  } else {
    combined = cleanedAnswer;
  }

  // Use WhatsApp's native URL
  const shareUrl = `https://wa.me/?text=${encodeURIComponent(combined)}`;
  window.open(shareUrl, "_blank", "noopener,noreferrer");
}

/* ---------- SpeechRecognition ---------- */
function startListening() {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition || null;
  if (!SR) {
    alert("Voice not supported in this browser. Use Chrome or Edge.");
    return;
  }
  if (recognitionRef.current) {
    try { recognitionRef.current.stop(); } catch {}
    recognitionRef.current = null;
  }
  const rec = new SR();
  rec.lang = "en-IN";
  rec.interimResults = true;
  rec.continuous = false;
  rec.maxAlternatives = 1;
  let finalTranscript = "";
  rec.onstart = () => setIsListening(true);
  rec.onresult = (ev) => {
    let interim = "";
    for (let i = ev.resultIndex; i < ev.results.length; ++i) {
      const r = ev.results[i];
      if (r.isFinal) finalTranscript += r[0].transcript;
      else interim += r[0].transcript;
    }
    setInput(finalTranscript || interim);
  };
  rec.onerror = (ev) => {
    console.warn("STT error", ev);
    setIsListening(false);
    // do not spam alerts for non-fatal issues
    if (ev && ev.error && ev.error !== "no-speech") {
      alert("Voice error: " + (ev.error || "unknown"));
    }
  };
  rec.onend = () => setIsListening(false);
  recognitionRef.current = rec;
  try { rec.start(); } catch (e) { console.error("rec.start", e); setIsListening(false); }
}
function stopListening() {
  if (recognitionRef.current) {
    try { recognitionRef.current.stop(); } catch {}
    recognitionRef.current = null;
  }
  setIsListening(false);
}

/* ---------- Text-to-Speech (unchanged) ---------- */
/* include your existing ensureVoicesLoaded, speakText, stopSpeaking functions here */

/* Helper to grab the last user question text from a session (returns full text if present) */
const lastUserQuestionFull = (session) => {
  try {
    if (!session || !session.messages) return "";
    for (let i = session.messages.length - 1; i >= 0; i--) {
      const m = session.messages[i];
      if (m.isUser && m.text) {
        return m.text; // return full text exactly as stored
      }
    }
    return session.messages[0]?.text || "";
  } catch {
    return "";
  }
};





  /* ---------- UI safe helpers (unchanged) ---------- */
const lastUserQuestionSafe = (session) => {
  try {
    if (!session || !session.messages) return "";

    for (let i = session.messages.length - 1; i >= 0; i--) {
      const m = session.messages[i];

      if (m.isUser && m.text) {
        const text = m.text.trim();
        return text.length > 40 ? text.slice(0, 40) + "..." : text;
      }
    }

    const first = session.messages[0]?.text || "";
    return first.length > 40 ? first.slice(0, 40) + "..." : first;
  } catch {
    return "";
  }
};

function isFollowUp(text = "") {
  const t = text.toLowerCase().trim();

  return (
    /^(continue|go on|next)$/.test(t) ||
    /(explain more|explain step by step|elaborate further|continue explanation)/.test(t) ||

    // 🔥 NEW: pattern-based follow-ups
    /(same pattern|same format|same model|similar paper|same type)/.test(t)
  );
}

 

/* ---------- handleSend (UPDATED FOR MULTIMODAL) ---------- */
 

async function handleSend() {
  if (!input.trim() && files.length === 0) return;

  // ===============================
  // UI FILE PREVIEW (BASE64 ONLY)
  // ===============================
  const storedFiles = await Promise.all(
    files.map(
      (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () =>
            resolve({
              name: file.name,
              type: file.type,
              size: file.size,
              url: reader.result,
            });
          reader.onerror = reject;
          reader.readAsDataURL(file);
        })
    )
  );

  const rawText = input.trim();
  const displayText = removeAutoSymbols(rawText);

  // ===============================
  // USER MESSAGE
  // ===============================
  const userMsg = {
    id: `u-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    isUser: true,
    text: displayText,
    files: storedFiles,
    ts: Date.now(),
  };

  appendToSession(userMsg);

  setInput("");
  setPreviews([]);
  setIsLoading(true);

  try {
    let aiText = null;

    // =====================================================
    // ✅ SIMPLE ARITHMETIC (LOCAL)
    // =====================================================
    if (/^[0-9\s()+\-*/%.]+$/.test(rawText)) {
      try {
        aiText = `FINAL ANSWER: ${Function(
          `"use strict";return (${rawText})`
        )()}`;
      } catch {
        aiText = null;
      }
    }

    // =====================================================
    // ✅ FOLLOW-UP LOGIC (CHATGPT STYLE)
    // =====================================================
    let finalPrompt = rawText;

    if (isFollowUp(rawText)) {
      const lastQuestion = [...messages]
        .reverse()
        .find((m) => m.isUser && m.text !== rawText);

      if (lastQuestion) {
        finalPrompt = `
Previous Question:
${lastQuestion.text}

User says: "${rawText}"

Explain the above question step by step with full working.
`.trim();
      }
    }

    // =====================================================
    // ✅ GEMINI CALL
    // =====================================================
    if (!aiText) {
      aiText = await getAcademicAnswer(finalPrompt, files);
    }

    // ===============================
    // AI MESSAGE
    // ===============================
    const aiMsg = {
      id: `ai-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      isUser: false,
      text: aiText,
      ts: Date.now(),
      prompt: rawText,
    };

    appendToSession(aiMsg);

  } finally {
    setIsLoading(false);
    setFiles([]);
  }
}

const handleWelcomePrompt = (prompt) => {
  setInput(prompt);
};



//Handle End

  function scrollFullyToBottom() {
    // run AFTER React paints and DOM expands
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        try {
          if (scrollRef.current) {
            const container = scrollRef.current;
            container.scrollTo({
              top: container.scrollHeight,
              behavior: "smooth",
            });
          } else {
            // fallback (should rarely happen)
            window.scrollTo({
              top: document.documentElement.scrollHeight,
              behavior: "smooth",
            });
          }
        } catch (e) {
          console.warn("scrollFullyToBottom failed", e);
        }
      });
    });
  }


// Handle Function End

function onKeyDown(e) {
  if (e.key === "Enter" && !e.shiftKey) {
    e.preventDefault();
    handleSend();
  }
}

/* ---------- Image zoom + in-image chat bubble component ---------- */
/**
 * Usage: <ImageZoomWithChat thumb={thumbUrl} full={fullUrl} chatHtml={<div>chat content</div>} />
 * - thumb: thumbnail URL
 * - full: full url (can be same as thumb)
 * - chatHtml: JSX or string for in-image chat content
 */
function ImageZoomWithChat({ thumb, full, chatHtml }) {
  const [open, setOpen] = useState(false);
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const dragRef = useRef({ dragging: false, sx: 0, sy: 0, ox: 0, oy: 0 });

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const onWheel = (e) => {
    // only handle when overlay is open
    if (!open) return;
    e.preventDefault();
    const delta = -e.deltaY * 0.0016;
    setScale((s) => Math.min(4, Math.max(0.6, +(s + delta).toFixed(3))));
  };

  // pointer/touch friendly drag handlers
  const startDrag = (e) => {
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    dragRef.current.dragging = true;
    dragRef.current.sx = clientX;
    dragRef.current.sy = clientY;
    dragRef.current.ox = pos.x;
    dragRef.current.oy = pos.y;
    document.body.style.cursor = "grabbing";
    // attach global move/end handlers for reliability
    window.addEventListener("pointermove", moveDrag, { passive: false });
    window.addEventListener("pointerup", endDrag, { passive: true });
    window.addEventListener("touchmove", moveDrag, { passive: false });
    window.addEventListener("touchend", endDrag, { passive: true });
  };
  const moveDrag = (e) => {
    if (!dragRef.current.dragging) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const dx = clientX - dragRef.current.sx;
    const dy = clientY - dragRef.current.sy;
    setPos({ x: dragRef.current.ox + dx, y: dragRef.current.oy + dy });
    e.preventDefault?.();
  };
  const endDrag = () => {
    dragRef.current.dragging = false;
    document.body.style.cursor = "auto";
    try {
      window.removeEventListener("pointermove", moveDrag);
      window.removeEventListener("pointerup", endDrag);
      window.removeEventListener("touchmove", moveDrag);
      window.removeEventListener("touchend", endDrag);
    } catch {}
  };

  // Reset view when closed
  useEffect(() => {
    if (!open) {
      setScale(1);
      setPos({ x: 0, y: 0 });
      dragRef.current = { dragging: false, sx: 0, sy: 0, ox: 0, oy: 0 };
    }
  }, [open]);


  const reset = () => { setScale(1); setPos({ x: 0, y: 0 }); };

  // inline CSS used same approach as other components
  const css = `
    .zz-thumb-wrapper { position: relative; display:inline-block; cursor: zoom-in; }
    .zz-thumb-img { width: 80px; height: 80px; object-fit: cover; border-radius:6px; }
    .zz-thumb-chat { position: absolute; left:8px; bottom:8px; background: rgba(255,255,255,0.95); padding:6px 8px; border-radius:8px; font-size:12px; box-shadow: 0 6px 18px rgba(0,0,0,0.06); max-width:66%; }
    .zz-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 14000; display:flex; align-items:center; justify-content:center; }
    .zz-controls { position: fixed; top: 18px; right: 18px; display:flex; gap:8px; z-index: 14010; }
    .zz-controls button { padding:6px 10px; border-radius:8px; cursor:pointer; }
    .zz-container { max-width: 90%; max-height: 86%; overflow: hidden; background: #fff; padding: 12px; border-radius: 12px; position: relative; display:flex; align-items:center; justify-content:center; }
    .zz-image { max-width: 100%; max-height: 100%; user-select: none; transition: transform 0.04s linear; will-change: transform; cursor: grab; }
    .zz-in-chat { position: absolute; z-index: 14005; background: rgba(255,255,255,0.98); padding: 10px 12px; border-radius: 12px; box-shadow: 0 8px 24px rgba(0,0,0,0.15); max-width: 360px; font-size: 14px; cursor: grab; user-select: none; }
  `;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: css }} />
      <div className="zz-thumb-wrapper" onClick={() => setOpen(true)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter") setOpen(true); }}>
        <img src={sanitizeImageUrl(thumb)} alt="attachment-thumb" className="zz-thumb-img" draggable={false} />
        {chatHtml ? <div className="zz-thumb-chat">{typeof chatHtml === "string" ? <div dangerouslySetInnerHTML={{ __html: chatHtml }} /> : chatHtml}</div> : null}
      </div>

      {open && (
        <div
          className="zz-overlay"
          onMouseMove={moveDrag}
          onMouseUp={endDrag}
          onMouseLeave={endDrag}
          onTouchMove={moveDrag}
          onTouchEnd={endDrag}
          role="dialog"
          aria-modal="true"
        >
          <div className="zz-controls">
            <button onClick={() => setScale((s) => Math.min(4, +(s + 0.25).toFixed(2)))}>＋</button>
            <button onClick={() => setScale((s) => Math.max(0.6, +(s - 0.25).toFixed(2)))}>－</button>
            <button onClick={reset}>Reset</button>
            <button onClick={() => setOpen(false)}>Close ✕</button>
          </div>

          <div className="zz-container" onWheel={(e) => { onWheel(e); }} onMouseDown={startDrag} role="presentation" onTouchStart={startDrag}>
            <img
              src={sanitizeImageUrl(full)}
              alt="zoomed"
              className="zz-image"
              draggable={false}
              style={{ transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})` }}
            />

            {chatHtml && (
              <div
                className="zz-in-chat"
                onMouseDown={(e) => {
                  // prevent main drag while moving chat bubble
                  e.stopPropagation();
                  const box = e.currentTarget;
                  const startX = e.clientX;
                  const startY = e.clientY;
                  const rect = box.getBoundingClientRect();
                  const origLeft = rect.left;
                  const origTop = rect.top;

                  function onMove(me) {
                    try {
                      box.style.left = `${origLeft + (me.clientX - startX)}px`;
                      box.style.top = `${origTop + (me.clientY - startY)}px`;
                    } catch {}
                  }
                  function onUp() { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); }
                  window.addEventListener("mousemove", onMove);
                  window.addEventListener("mouseup", onUp);
                }}
                style={{ right: 28, bottom: 28 }}
              >
                {typeof chatHtml === "string" ? <div dangerouslySetInnerHTML={{ __html: chatHtml }} /> : chatHtml}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

const TEXTAREA_MAX = 200; // px — must match CSS max-height for desktop; mobile uses CSS override
const resizeTextarea = (el) => {
  if (!el) return;
  // reset then set to scrollHeight capped so it expands smoothly and no inner scrollbar until cap
  try {
    el.style.height = "auto";
    const h = Math.min(el.scrollHeight, TEXTAREA_MAX);
    el.style.height = h + "px";
  } catch {}
};

  /* ---------- Small animated dots indicator & rest of UI ---------- */
  function AnimatedDots({ size = 8, gap = 6 }) {
    const inline = `
      .aa-animated-dots { display:inline-flex; align-items:center; gap:${gap}px; margin-left:8px; }
      .aa-animated-dots .dot {
        width:${size}px; height:${size}px; border-radius:50%; background:currentColor; opacity:0.18;
        transform: translateY(0);
        animation: aa-dot-bounce 900ms infinite ease-in-out;
      }
      .aa-animated-dots .dot:nth-child(1) { animation-delay: 0ms; }
      .aa-animated-dots .dot:nth-child(2) { animation-delay: 150ms; }
      .aa-animated-dots .dot:nth-child(3) { animation-delay: 300ms; }
      @keyframes aa-dot-bounce {
        0% { opacity:0.18; transform: translateY(0); }
        40% { opacity:1; transform: translateY(-6px); }
        80% { opacity:0.3; transform: translateY(0); }
        100% { opacity:0.18; transform: translateY(0); }
      }
      .aa-thinking-row {
        display: flex;
        align-items: center;
        gap: 8px;
        padding-top:0px;   
        padding-bottom: 400px;   
        color: #000; 
      }
    `;
    return (
      <>
        <style dangerouslySetInnerHTML={{ __html: inline }} />
        <span className="aa-animated-dots" aria-hidden>
          <span className="dot" />
          <span className="dot" />
          <span className="dot" />
        </span>
      </>
    );
  }

  const splashInlineCSS = `
    .aa-splash { position: fixed; inset: 0; display:flex; align-items:center; justify-content:center; flex-direction:column; background: linear-gradient(180deg, #0f172a 0%, #020617 100%); color: #f8fafc; z-index: 99999; font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial; }
    .aa-splash-brand { font-size: 28px; letter-spacing: 1px; margin-bottom: 8px; }
    .aa-splash-sub { font-size: 14px; opacity: 0.9; }
  `;

  if (showSplash) {
    return (
      <div>
        <style dangerouslySetInnerHTML={{ __html: splashInlineCSS }} />
        <div className="aa-splash" role="status" aria-live="polite">
          <div className="aa-splash-brand">IndLawAI</div>
          <div className="aa-splash-sub">AI Powered Legal Assistant</div>
        </div>
      </div>
    );
  }

  /* ---------- Full UI render (same layout as before) ---------- */
  return (
    
  <div className="aa-full" role="application" aria-label="Academic AI Application">
    <div className="aa-card" ref={aaCardRef} role="main">
      <aside className="aa-left" aria-hidden={false}>
        <div className="aa-sidebar-header-premium-minimal">
          <div className="header-branding-icon">
            <span className="material-icons title-glyph">history</span>
          </div>

          <div className="aa-header-actions-icons">
            <button 
              className="icon-action-btn primary-glow" 
              onClick={() => newSessions()} 
              title="New Chat"
            >
              <span className="material-icons">add_circle</span>
            </button>

            <button 
              className="icon-action-btn destructive-minimal" 
              onClick={openDeleteAllModal} 
              title="Clear All History"
            >
              <span className="material-icons">delete_sweep</span>
            </button>
          </div>

          <DangerModal
            open={showDeleteAllModal}
            title="Clear All History?"
            description="This will permanently remove all your legal chats and uploaded documents. This action cannot be undone."
            confirmLabel="Clear History"
            cancelLabel="Keep Chats"
            onConfirm={confirmDeleteAll}
            onCancel={closeDeleteAllModal}
          />
        </div>

        <div className="aa-history-list-premium">
  {sessions.length === 0 ? (
    <div className="history-empty-status">No recent activity</div>
  ) : (
    /* We use the helper function to group sessions by date */
    Object.entries(groupSessionsByDate(sessions)).map(([groupName, groupSessions]) => (
      groupSessions.length > 0 && (
        <div key={groupName} className="aa-history-group-container">
          {/* Group Heading (Today, Yesterday, etc.) */}
          <h6 className="premium-group-label-bold">
            <strong>{groupName}</strong>
          </h6>
          
          {groupSessions
            .sort((a, b) => b.createdAt - a.createdAt)
            .map((s) => {
              // Dynamically determine icon based on the question content
              const userQuestion = lastUserQuestionSafe(s) || "";
              const subjectIcon = getSubjectIcon(userQuestion);

              return (
                <div
                  key={s.id}
                  className={`premium-session-card ${s.id === currentSessionId ? "is-active" : ""}`}
                  onClick={() => openSession(s.id)}
                >
                  <div className="session-card-inner">
                    <div className="session-meta-header">
                      <span className="premium-date-bold" style={{ fontWeight: '700' }}>
                        {groupName === "Today" 
                          ? new Date(s.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                          : new Date(s.createdAt).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                      </span>
                      
                      <button
                        className={`premium-delete-btn ${confirmingDelete === s.id ? "is-confirming" : ""}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (confirmingDelete === s.id) {
                            deleteSession(s.id);
                            setConfirmingDelete(null);
                          } else {
                            setConfirmingDelete(s.id);
                            // Auto-reset after 3 seconds if not clicked again
                            setTimeout(() => setConfirmingDelete(null), 3000);
                          }
                        }}
                        title={confirmingDelete === s.id ? "Click again to confirm" : "Delete Session"}
                      >
                        <span className="material-icons" style={{ fontSize: '18px' }}>
                          {confirmingDelete === s.id ? "done" : "delete_outline"}
                        </span>
                        {confirmingDelete === s.id && <span className="confirm-text">Confirm?</span>}
                      </button>

                    </div>

                    <div className="session-content-preview">
                      {/* Dynamic Subject Icon */}
                      <span className="material-icons icon-visible">{subjectIcon}</span>
                      <p className="preview-text-line">
                        {userQuestion || "New Discussion"}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      )
    ))
  )}
</div>

        <div
          className={` ${leftFooterStyle && leftFooterStyle.left ? "fixed" : ""}`}
          style={
            leftFooterStyle && leftFooterStyle.left
              ? {
                  left: leftFooterStyle.left,
                  width: leftFooterStyle.width,
                  bottom: leftFooterStyle.bottom,
                  zIndex: leftFooterStyle.zIndex,
                }
              : {}
          }
        >
          <div className="aa-left-footer">
            <div className="aa-left-footer-inner">

              <div className="muted small">
                Crafted By{" "}
                <a
                  href="https://fftech.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-link"
                >
                  FFT
                </a>
              </div> 

              <div className="muted small">
                <a
                  href="https://fftech.in/indlawai-privacy-policy.php"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-link"
                >
                  Privacy Policy
                </a>
              </div>

            </div>
          </div>
          
        </div>
      </aside>

      <main className="aa-main" role="region" aria-label="Chat area">
        <div className="aa-header">
          <div className="aa-brand-premium">
              <div className="logo-wrapper-elevated">
                <img
                  src="./logo.png"
                  alt="IndLawAI - AI Powered Legal Assistant"
                  className="brand-logo-img"
                />
                <div className="logo-glass-shine"></div>
              </div>
              
              <div className="brand-text-block">
                <h1 className="brand-title-main">
                  <span className="accent-color">IndLawAI</span>
                </h1>

                <div className="brand-tagline">
                  <span className="tagline-dot"></span>
                  Know Your Rights
                </div>
              </div>
            </div>



          <div className="header-action-group">

              {/* Google Play */}
              <a
                href="https://play.google.com/store/apps/details?id=com.indlaw_ai.mobile"
                target="_blank"
                rel="noopener noreferrer"
                className="playstore-btn"
              >
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                  alt="Google Play"
                />
              </a>

              {/* Menu */}
              <button
                className="mobile-history-btn"
                onClick={() => setShowHistoryMobile(true)}
              >
                <span className="material-icons">menu</span>
              </button>

            </div>

        <FeedbackModal
          open={showFeedback}
          feedbackText={feedbackText}
          setFeedbackText={setFeedbackText}
          feedbackType={feedbackType}
          setFeedbackType={setFeedbackType}
          onClose={() => setShowFeedback(false)}
          onSubmit={handleSubmitFeedback}
        />
        {showFeedbackSuccess && (
        <div className="feedback-success-toast">
          <span className="material-icons">
            check_circle
          </span>

          <div>
            <strong>Feedback Submitted</strong>
            <div>Thank you for helping improve IndLawAI.</div>
          </div>
        </div>
      )}

      </div>   
            
            
        <div className="answer-wrapper">

          {messages.length === 0 ? (

            /* ===================== WELCOME SCREEN ===================== */
            <WelcomePanel
              onStart={(prompt) => {
                setInput(prompt);
              }}
            />

          ) : (

    /* ===================== CHAT SCREEN ===================== */
        <div className="answer-scroll" ref={scrollRef} aria-live="polite">

          {messages.map((m) => (
            <div key={m.id} className="aa-message-row" data-msgid={m.id}>

              {m.isUser ? (
                <div className="user-bubble" aria-live="polite">
                  <div className="user-bubble-inner">

                    {/* Floating Actions */}
                    <div className="user-msg-actions">

                      <button
                        type="button"
                        className="icon-btn"
                        title="Copy"
                        onClick={async (e) => {
                          e.stopPropagation();
                          await handleCopyText(m.text || "", setShowCopyToast);
                        }}
                      >
                        <span className="material-icons">content_copy</span>
                      </button>

                      <button
                        type="button"
                        className="icon-btn"
                        title="Edit"
                        onClick={(e) => {
                          e.stopPropagation();

                          const txt = m.text || "";
                          if (!txt) return;

                          setInput(txt);

                          composerAnchorRef.current?.scrollIntoView({
                            behavior: "smooth",
                            block: "end",
                          });
                        }}
                      >
                        <span className="material-icons">edit</span>
                      </button>

                    </div>

                    {/* User Message */}
                    <div className="user-text-wrapper">
                      <div className="user-text">
                        <ReactMarkdown
                          remarkPlugins={[remarkMath]}
                          rehypePlugins={[rehypeKatex]}
                        >
                          {(m.text || "").trim()}
                        </ReactMarkdown>
                      </div>
                    </div>

                    {/* Attachments */}
                    {m.files?.length > 0 && (
                      <div className="attachments">
                        {m.files.map((f, idx) =>
                          f.type?.includes("pdf") ? (
                            <div key={idx} className="pdf-chip">
                              📄 {f.name}
                            </div>
                          ) : (
                            <ImageZoomWithChat
                              key={idx}
                              thumb={f.url}
                              full={f.url}
                              chatHtml={null}
                            />
                          )
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="ai-bubble">

  {/* ================= MAIN AI ANSWER ================= */}
  {m.text ? (
      renderFormattedText(m.text)
    ) : (
      <div className="muted">
        No response generated.
      </div>
    )}

  {/* ================= QUIZ CARD ================= */}
  {m.quiz && (
  <div ref={quizRef} className="aa-quiz-premium-card" aria-live="polite">
    <div className="quiz-header">
      <span className="quiz-badge">QUICK QUIZ</span>
      <span className="quiz-icon">🎯</span>
    </div>
    
    <div className="quiz-question-text">
      <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
        {m.quiz.question}
      </ReactMarkdown>
    </div>

    <div className="quiz-options-grid">
      {m.quiz.options.map((opt, idx) => {
        const selected = quizResponses[m.id];
        const answered = typeof selected !== "undefined";
        const isCorrect = idx === m.quiz.correctIndex;
        const isSelected = selected === idx;

        let stateClass = "";
        if (answered) {
          if (isCorrect) stateClass = "is-correct";
          else if (isSelected) stateClass = "is-wrong";
          else stateClass = "is-disabled";
        }

        return (
          <button
            key={idx}
            className={`quiz-option-item ${stateClass}`}
            onClick={() => !answered && recordQuizResponse(m.id, idx)}
            disabled={answered}
          >
            {/* Professional Option Letters */}
            <span className="option-prefix">{String.fromCharCode(65 + idx)}</span>
            
            <div className="option-content">
              <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                {opt}
              </ReactMarkdown>
            </div>
            
            {answered && isCorrect && <span className="material-icons feedback-icon">check_circle</span>}
            {answered && isSelected && !isCorrect && <span className="material-icons feedback-icon">cancel</span>}
          </button>
        );
      })}
    </div>

    {/* Feedback Section with Animation */}
    {typeof quizResponses[m.id] !== "undefined" && (
      <div className={`quiz-feedback-banner ${quizResponses[m.id] === m.quiz.correctIndex ? 'success' : 'error'}`}>
        <div className="feedback-content">
           <span className="feedback-text">
             {quizResponses[m.id] === m.quiz.correctIndex ? "Excellent! You've mastered this concept." : "Keep learning! Check the correct formula above."}
           </span>
        </div>
      </div>
    )}
  </div>
)}

        {/* ================= ALWAYS-VISIBLE ACTION BAR ================= */}
        <div className="ai-message-actions-wrap">
            {/* Left Group: Primary Actions */}
             

            {/* Right Group: Utility Actions */}
            <div className="message-actions">
              <button
                className={`action-btn like-btn ${
                  selectedVote[m.id] === "like" ? "active" : ""
                }`}
                onClick={() =>
                  setSelectedVote(prev => ({
                    ...prev,
                    [m.id]: "like"
                  }))
                }
              >
                <span className="material-icons">thumb_up</span>
              </button>

              <button
                className={`action-btn dislike-btn ${
                  selectedVote[m.id] === "dislike" ? "active" : ""
                }`}
                onClick={() =>
                  setSelectedVote(prev => ({
                    ...prev,
                    [m.id]: "dislike"
                  }))
                }
              >
                <span className="material-icons">thumb_down</span>
              </button>

              <button
                className="action-btn whatsapp"
                onClick={() => handleWhatsAppShare(m.text)}
                title="WhatsApp"
              >
                <i className="fab fa-whatsapp"></i>
              </button>

              <button
                className="action-btn x-btn"
                onClick={() =>
                  window.open(
                    `https://twitter.com/intent/tweet?text=${encodeURIComponent(m.text || "")}`,
                    "_blank"
                  )
                }
                title="Share on X"
              >
                <i className="fab fa-x-twitter"></i>
              </button>

              <button
                className="action-btn"
                title="Copy"
                onClick={async () => {
                  const ok = await handleCopyText(
                    m.text || "",
                    setShowCopyToast
                  );

                  if (ok) {
                    setCopiedMsgId(m.id);
                    setTimeout(() => setCopiedMsgId(null), 1200);
                  }
                }}
              >
                <span className="material-icons">
                  {copiedMsgId === m.id ? "check" : "content_copy"}
                </span>
              </button>
            </div>



          </div>
        
      </div>

              )}

            </div>
          ))}

          {isLoading && (
            <div
              className="aa-thinking-row aithinking"
              role="status"
              aria-live="polite"
            >
              <span>IndLawAI is thinking</span>
              <AnimatedDots />
            </div>
          )}

        </div>
      )}
    </div>


        <div className="composer-wrapper-premium" ref={composerAnchorRef}>
          <div className="composer-panel-premium">
            
            {/* The Input Pill */}
            <div className={`input-pill-container ${previews.length > 0 ? 'has-attachment' : ''}`}>
              
              {/* 1. SINGLE IMAGE PREVIEW (Shows only if 1 image exists) */}
              {previews.length > 0 && (
                <div className="single-preview-box">
                  <img 
                    src={sanitizeImageUrl(previews[0].url, previews[0].name)} 
                    alt="Upload preview" 
                  />
                  <button 
                    className="remove-attachment-btn" 
                    onClick={() => removePreview(previews[0].name, previews[0].size)}
                    title="Remove image"
                  >
                    <span className="material-icons">cancel</span>
                  </button>
                </div>
              )}

              <input
                id="composer-file-input"
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                accept="image/jpeg,image/png"
                onChange={handleFileChange}
                multiple={false} /* Strictly single file */
              />
              
              {/* 2. CAMERA/ATTACH (Hidden if image is already attached) */}
              {previews.length === 0 && (
                <label htmlFor="composer-file-input" className="pill-icon-btn camera-btn">
                  <span className="material-icons">photo_camera</span>
                </label>
              )}

              {/* 3. VOICE/MIC */}
              <button 
                onClick={() => (isListening ? stopListening() : startListening())}
                className={`pill-icon-btn mic-btn ${isListening ? 'active' : ''}`}
              >
                <span className="material-icons">{isListening ? "mic_off" : "mic"}</span>
              </button>

              {/* 4. TEXT AREA */}
              <textarea
                ref={(el) => el && resizeTextarea(el)}
                className="premium-textarea"
                placeholder="Ask about Indian laws..."
                value={input}
                rows="1"
                onChange={(e) => setInput(e.target.value)}
                onInput={(e) => {
                  // Only auto-resize if we are NOT on a small mobile screen
                  if (window.innerWidth > 480) {
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                  }
                }}
                onKeyDown={onKeyDown}
              />

              {/* 5. SEND BUTTON */}
              <button 
                onClick={handleSend} 
                disabled={!input.trim() && previews.length === 0}
                className="premium-send-btn"
              >
                <span className="material-icons">send</span>
              </button>
            </div>
            <div className="muted small composer-note">IndLawAI can make mistakes. Verify legal information.</div>
          </div>
        </div>


      </main>
    </div>

    {showHistoryMobile && (
      
  <div className="mobile-history-premium">
    <div className="mobile-history-header">
      <h6 className="mobile-premium-title">History</h6>

      <div style={{ position: "relative" }} ref={socialMenuRef}>
            <button
                type="button"
                className="indlawai-icon-btn"
                onClick={() => setShowSocialMenu((v) => !v)}
                aria-label="IndLawAI Links"
                title="IndLawAI Links"
                style={{
                  border: "none",
                  padding: "0",
                  marginRight: "8px",
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",

                  /* IndLawAI Dark Blue Theme */
                  backgroundImage: "linear-gradient(135deg, #123D6B, #1E4E8C)",

                  boxShadow: "0 4px 12px rgba(18, 61, 107, 0.25)",
                  cursor: "pointer",
                  transition: "transform 0.15s ease",
                }}
              onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.92)")}
              onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
              onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
            >
              <span className="material-icons" style={{ fontSize: "22px", color: "#fff" }}>
                language
              </span>
            </button>
            
              {showSocialMenu && (
                <div
                  style={{
                    position: "absolute",
                    right: 0,
                    top: "48px",
                    minWidth: "220px",
                    background: "#fff",
                    borderRadius: "14px",
                    boxShadow: "0 12px 30px rgba(0,0,0,0.18)",
                    padding: "6px 0",
                    zIndex: 9999,
                  }}
                >
                  {[
                    { label: "FFT Website", url: "https://fftech.in", icon: "language" },
                    { label: "Instagram", url: "https://www.instagram.com/indlawai/", icon: "photo_camera" },
                    { label: "Facebook", url: "https://www.facebook.com/indlawai", icon: "facebook" },
                    { label: "LinkedIn", url: "https://www.linkedin.com/company/indlawai", icon: "business" }, 

                    // ✅ WhatsApp Channel (SVG icon)
                    {
                      label: "WhatsApp",
                      url: "https://whatsapp.com/channel/0029Vb7iGPaKQuJKrv220q03",
                      highlight: true,
                    },
                  ].map((item) => (
                    <div
                      key={item.label}
                      onClick={() => {
                        window.open(item.url, "_blank");
                        setShowSocialMenu(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "11px 14px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: item.highlight ? "600" : "500",
                        color: "#111",
                        background: item.highlight ? "#E8F5E9" : "transparent",
                        borderRadius: "8px",
                        margin: "2px 6px",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = item.highlight ? "#DCF1E2" : "#f3f4f6")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = item.highlight ? "#E8F5E9" : "transparent")
                      }
                    >
                      {/* LEFT SIDE ICON + LABEL */}
                      <span style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1 }}>
                        {item.highlight ? (
                          // ✅ WhatsApp SVG ICON
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 32 32"
                            fill="#25D366"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path d="M16.04 2C8.3 2 2 8.3 2 16.04c0 2.83.75 5.47 2.06 7.77L2 30l6.41-2.01a14 14 0 0 0 7.63 2.05h.01C23.7 30.04 30 23.74 30 16.04 30 8.3 23.7 2 16.04 2zm8.1 19.31c-.34.96-1.97 1.85-2.71 1.97-.71.11-1.61.16-2.6-.16-.6-.19-1.36-.44-2.34-.86-4.11-1.78-6.79-5.95-7-6.23-.21-.27-1.67-2.23-1.67-4.25 0-2.02 1.06-3.01 1.44-3.42.38-.41.83-.52 1.11-.52.28 0 .56 0 .8.01.26.01.6-.1.94.72.34.83 1.17 2.87 1.27 3.08.11.21.17.46.03.74-.14.27-.21.44-.41.68-.21.24-.44.53-.62.71-.21.21-.43.43-.19.85.24.41 1.06 1.75 2.27 2.83 1.56 1.39 2.88 1.82 3.29 2.03.41.21.65.18.89-.11.24-.28 1.02-1.19 1.29-1.6.27-.41.55-.34.92-.21.38.14 2.4 1.13 2.81 1.33.41.21.68.31.78.48.1.17.1.98-.24 1.94z"/>
                          </svg>
                        ) : (
                          // ✅ Material Icons for others
                          <span
                            className="material-icons"
                            style={{ fontSize: "18px", color: "#444" }}
                          >
                            {item.icon}
                          </span>
                        )}

                        {item.label}
                      </span>

                      {/* JOIN BADGE */}
                      {item.highlight && (
                        <span
                          style={{
                            fontSize: "11px",
                            background: "#25D366",
                            color: "#fff",
                            padding: "2px 8px",
                            borderRadius: "10px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          JOIN
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}


            </div>  

          <button
                  className="feedback-btn"
                  onClick={() => setShowFeedback(true)}
                >
                  <span className="material-icons">campaign</span>
                </button>    


      <button
        className="mobile-close-btn"
        onClick={() => {
          setShowHistoryMobile(false);
          setConfirmingDelete(null); // Reset safety state on close
        }}
      >
        <span className="material-icons">close</span>
      </button>
    </div>

    <div className="mobile-history-list">
      {sessions.length === 0 ? (
        <div className="mobile-empty-state">
          <span className="material-icons">history_edu</span>
          <p>No recent activity found</p>
        </div>
      ) : (
        Object.entries(groupSessionsByDate(sessions)).map(([groupName, groupSessions]) => (
          groupSessions.length > 0 && (
            <div key={groupName} className="mobile-group-section">
              {/* Bold Group Label */}
              <div className="mobile-group-label">
                <strong>{groupName}</strong>
              </div>

              {groupSessions
                .sort((a, b) => b.createdAt - a.createdAt)
                .map((s) => {
                  const userQuestion = lastUserQuestionSafe(s) || "";
                  const subjectIcon = getSubjectIcon(userQuestion);
                  const isActive = s.id === currentSessionId;

                  return (
                    <div
                      key={s.id}
                      className={`mobile-session-card ${isActive ? "is-active" : ""}`}
                      onClick={() => openSession(s.id)}
                    >
                      <div className="mobile-card-main">
                        <div className="mobile-card-icon">
                          <span className="material-icons">{subjectIcon}</span>
                        </div>
                        <div className="mobile-card-details">
                          <div className="mobile-card-timestamp">
                            <strong>
                              {groupName === "Today"
                                ? new Date(s.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                : new Date(s.createdAt).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                            </strong>
                          </div>
                          <div className="mobile-card-preview">
                            {userQuestion || "New Discussion"}
                          </div>
                        </div>
                      </div>

                      <div className="mobile-card-actions">
                        <button
                          className={`mobile-delete-action ${confirmingDelete === s.id ? "confirming" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (confirmingDelete === s.id) {
                              deleteSession(s.id);
                              setConfirmingDelete(null);
                            } else {
                              setConfirmingDelete(s.id);
                              setTimeout(() => setConfirmingDelete(null), 3000);
                            }
                          }}
                        >
                          <span className="material-icons">
                            {confirmingDelete === s.id ? "check_circle" : "delete_outline"}
                          </span>
                          {confirmingDelete === s.id && <span>Confirm</span>}
                        </button>
                      </div>
                    </div>
                  );
                })
              }
            </div>
          )
        ))
      )}

    </div>    
  </div>
)}


    <CopyToast visible={showCopyToast} />
  </div>
);
}